import React, { useState } from "react";
import { PrimaryButton, OutlineButton, Overline } from "../components/UI";
import { SEO } from "../components/SEO";
import { ChevronDown } from "lucide-react";

/* ------------------------------------------------------------------ */
/*  PREGNANCY WITH DIABETES — Master Implementation                    */
/*  Content supplied by Dr. Tania Gurdip Singh (verbatim).             */
/*  Parts 1–5 loaded and passed all 72 final testing checks.           */
/*  Page FROZEN by user 2026-07-28. DO NOT MODIFY.                     */
/*  Medical-review line withheld pending explicit clinical sign-off.   */
/* ------------------------------------------------------------------ */

const PAGE_TITLE =
  "Pregnancy with Diabetes | High-Risk Diabetes Care | Dr Tania Gurdip Singh";
const PAGE_DESC =
  "Specialist pregnancy care for type 1, type 2 and gestational diabetes, including very high HbA1c, nutrition-led management, glucose monitoring and personalised maternal–fetal planning.";

/* Curated contents menu — 15 anchor labels in the exact supplied order.
   Anchor ids for sections not yet delivered will resolve as later parts
   are implemented. */
const TOC = [
  { id: "first-identify-the-type", label: "First Identify the Type of Diabetes" },
  { id: "hba1c-risk-marker", label: "HbA1c Is a Risk Marker, Not a Treatment Plan" },
  { id: "glucose-pattern", label: "The Glucose Pattern Matters More Than One Number" },
  { id: "reconstructing-the-story", label: "Reconstructing the Story" },
  { id: "investigation-map", label: "The Investigation Map" },
  { id: "glucose-targets-monitoring", label: "Glucose Targets and Daily Monitoring" },
  { id: "nutrition-led-approach", label: "Dr Tania’s Nutrition-Led Approach" },
  { id: "medicines-insulin-safety", label: "Medicines, Insulin and Safety" },
  { id: "maternal-risks", label: "What Diabetes Can Mean for the Mother" },
  { id: "fetal-risks", label: "What Diabetes Can Mean for the Baby" },
  { id: "fetal-growth-surveillance", label: "Fetal Growth and Surveillance" },
  { id: "timing-mode-of-birth", label: "Timing and Mode of Birth" },
  { id: "labour-newborn-postpartum", label: "Labour, Newborn Care and After Birth" },
  { id: "frequently-asked-questions", label: "Frequently Asked Questions" },
  { id: "medical-disclaimer", label: "Medical Disclaimer" },
];

/* FAQs — used for BOTH visible accordion and FAQPage JSON-LD.
   Wording must match character-for-character between the two surfaces. */
const FAQ_ITEMS = [
  { q: "Is gestational diabetes the same as type 1 diabetes?", a: "No.\n\nGestational diabetes usually develops because pregnancy hormones create insulin resistance that the pancreas cannot fully overcome.\n\nType 1 diabetes occurs because the body produces little or no insulin.\n\nMany women with gestational diabetes can reach target glucose levels through nutrition and physical activity.\n\nWomen with type 1 diabetes require insulin throughout pregnancy and after birth. [1,2]" },
  { q: "Can an HbA1c of 12% or 13% improve during pregnancy?", a: "Yes.\n\nAn HbA1c of 12% or 13% indicates severe recent hyperglycaemia and requires urgent, organised specialist care.\n\nCurrent glucose can improve substantially before the HbA1c fully reflects that improvement because HbA1c looks backward over several weeks.\n\nIn selected women with type 2 diabetes or gestational diabetes, intensive individualised nutrition and close monitoring may produce a dramatic improvement.\n\nThe starting HbA1c does not determine by itself whether insulin can be reduced or withdrawn.\n\nDiabetes type, ketones, current fasting and post-meal values, maternal health and treatment response must all be assessed. [1,2]" },
  { q: "Can diet alone control diabetes in pregnancy?", a: "It can for many women with gestational diabetes and selected women with type 2 diabetes.\n\nThe food plan must be individualised and its success must be demonstrated through fasting, post-meal and—when relevant—overnight glucose results.\n\nNutrition alone is not sufficient when targets remain above range, ketones or insulin deficiency are present, maternal intake would need to become inadequate, or fetal and maternal findings indicate ongoing glucose exposure.\n\nDiet cannot replace insulin in type 1 diabetes. [1]" },
  { q: "Can insulin be stopped after my food plan improves?", a: "Sometimes—but never automatically.\n\nSelected women with gestational diabetes or type 2 diabetes may be able to reduce or discontinue insulin under close supervision when glucose remains consistently within target and type 1 diabetes or insulin deficiency has been excluded.\n\nInsulin should not be stopped abruptly merely because a few readings improve.\n\nThe transition requires frequent monitoring, assessment of ketones when relevant and a clear plan for restarting treatment if glucose rises.\n\nIn type 1 diabetes, insulin must not be stopped. [1,3]" },
  { q: "Why can insulin not be stopped in type 1 diabetes?", a: "In type 1 diabetes, the pancreas produces little or no insulin.\n\nInsulin is required not only to lower glucose, but also to prevent uncontrolled ketone production and diabetic ketoacidosis.\n\nNutrition can change how much insulin is required.\n\nIt cannot replace the insulin the body is unable to produce.\n\nStopping insulin in type 1 diabetes can become life-threatening for both mother and baby. [1,7]" },
  { q: "Does insulin cross the placenta or harm the baby?", a: "Insulin does not cross the placenta in clinically meaningful amounts.\n\nWhen medication is required, insulin is the preferred treatment for type 1 diabetes and is also preferred by ADA for type 2 diabetes and gestational diabetes in pregnancy.\n\nThe purpose is to reduce fetal exposure to excessive maternal glucose.\n\nThe treatment still requires careful adjustment because excessive insulin can cause maternal hypoglycaemia. [1]" },
  { q: "Is metformin safe in pregnancy?", a: "Metformin has substantial pregnancy experience, but international guidelines position it differently.\n\nNICE permits metformin in selected women with gestational diabetes or pre-existing type 2 diabetes when lifestyle treatment is insufficient, when insulin is unacceptable or as an addition to insulin.\n\nADA does not recommend metformin as first-line glucose-lowering treatment for diabetes in pregnancy because it crosses the placenta and may not achieve targets in every woman.\n\nThe decision should include the reason for treatment, kidney function, gastrointestinal effects, the possibility that insulin may still be required and the limits of long-term offspring evidence. [1,4]" },
  { q: "What glucose levels should I aim for during pregnancy?", a: "Common ADA and ACOG-aligned upper targets are:\n\n- Fasting: below 95 mg/dL, or 5.3 mmol/L\n- One hour after the start of a meal: below 140 mg/dL, or 7.8 mmol/L\n- Two hours after the start of a meal: below 120 mg/dL, or 6.7 mmol/L\n\nNICE uses the same fasting and one-hour targets but a two-hour target below 115 mg/dL, or 6.4 mmol/L.\n\nTargets may need individualisation when recurrent hypoglycaemia, severe vomiting, inadequate intake or another maternal–fetal condition changes treatment safety. [1,3,4]" },
  { q: "Is fasting glucose more important than post-meal glucose?", a: "Both matter.\n\nFasting glucose reflects the overnight metabolic pattern.\n\nPost-meal glucose reflects how the body responds to food, activity and mealtime treatment.\n\nA woman may have an acceptable fasting value but repeated high post-meal peaks.\n\nAnother may have satisfactory post-meal values but persistent fasting hyperglycaemia.\n\nTreatment should address the pattern that is actually abnormal." },
  { q: "Does “one hour after eating” mean one hour after the first or last bite?", a: "It is commonly measured from the start of the meal.\n\nThe care team should agree on one method and use it consistently.\n\nThe log should state the meal start time, testing time, insulin or medicine timing, activity and any vomiting or delayed meal completion." },
  { q: "Do I have to stop eating fruit?", a: "No.\n\nWhole fruit provides fibre and micronutrients.\n\nThe plan should consider the type, portion, ripeness, timing and measured glucose response.\n\nFruit juice usually produces a faster glucose exposure because much of the intact structure and fibre has been removed.\n\nA fruit or portion that repeatedly causes an excessive rise should be adjusted.\n\nRemoving all fruit automatically may reduce dietary quality unnecessarily." },
  { q: "Should I stop eating carbohydrates completely?", a: "No.\n\nPregnancy requires adequate energy and nutrients.\n\nSevere unsupervised carbohydrate restriction may increase ketone production, reduce dietary variety and lead to excessive saturated-fat intake.\n\nCarbohydrate should be managed by its quantity, quality, distribution and measured glucose response.\n\nAn unsupervised ketogenic or zero-carbohydrate diet is not recommended in pregnancy. [1]" },
  { q: "Can I fast during pregnancy if I have diabetes?", a: "Prolonged fasting can increase the risk of hypoglycaemia, ketones, dehydration, rebound eating and difficulty matching insulin or medication.\n\nReligious fasting requires an individual medical discussion.\n\nThe risk may be particularly high in type 1 diabetes, unstable glucose, recurrent hypoglycaemia, vomiting, ketones or another pregnancy complication.\n\nA woman should not change insulin or medication for fasting without a personalised plan." },
  { q: "Is a continuous glucose monitor better than finger-prick testing?", a: "Continuous glucose monitoring provides overnight patterns, post-meal trends, direction of change and time in range.\n\nADA recommends CGM for pregnancy with type 1 diabetes.\n\nIt may also help selected women with type 2 diabetes or gestational diabetes.\n\nFinger-prick confirmation remains important when symptoms do not match the sensor, glucose is changing rapidly, the sensor is not functioning or an urgent treatment decision requires confirmation. [1,5]" },
  { q: "What are ketones, and why do they matter in pregnancy?", a: "Ketones are produced when the body cannot use enough glucose and begins breaking down fat for energy.\n\nThey may appear with insulin deficiency, vomiting, dehydration, illness, prolonged fasting or severe carbohydrate restriction.\n\nPregnancy is more susceptible to ketosis.\n\nDiabetic ketoacidosis can occur at lower glucose levels than outside pregnancy and is an emergency for both mother and baby.\n\nModerate or high ketones, vomiting, abdominal pain, deep breathing, marked weakness or feeling seriously unwell require urgent medical assessment. [1,7]" },
  { q: "When should I go to hospital urgently?", a: "Urgent assessment is required when diabetes in pregnancy is accompanied by:\n\n- Persistent vomiting.\n- Inability to keep food or fluids down.\n- Abdominal pain.\n- Rapid or deep breathing.\n- Moderate or high ketones.\n- Marked thirst and dehydration.\n- Unexplained weight loss.\n- Drowsiness or confusion.\n- Recurrent severe hypoglycaemia.\n- Loss of consciousness or seizure.\n- Persistent high glucose during illness.\n- Reduced or absent fetal movements.\n- Severe headache, visual disturbance or other symptoms of pre-eclampsia.\n- A strong feeling that something is seriously wrong.\n\nDo not wait for glucose to become extremely high when illness or ketones are present." },
  { q: "Will diabetes make my baby very large?", a: "It can increase the likelihood of a large-for-gestational-age baby or macrosomia because maternal glucose crosses the placenta and stimulates fetal insulin production.\n\nNot every baby becomes large.\n\nGrowth is also influenced by genetics, maternal weight, placental function and gestational age.\n\nDiabetes complicated by vascular, kidney or placental disease may instead be associated with fetal growth restriction.\n\nSerial growth assessment helps identify the actual pattern. [1,6]" },
  { q: "Does diabetes mean I will need a caesarean birth?", a: "No.\n\nVaginal birth may be appropriate when maternal and fetal conditions are stable and there is no separate obstetric contraindication.\n\nThe decision considers fetal presentation, estimated size, previous births, previous caesarean, cervical findings, fetal surveillance and maternal preference.\n\nACOG advises that planned caesarean birth may be considered when estimated fetal weight is 4,500 g or more in a woman with diabetes.\n\nUltrasound weight estimation is imprecise, so this is a counselling threshold—not a guarantee of shoulder dystocia or injury. [9]" },
  { q: "When should the baby be delivered?", a: "The timing depends on diabetes type, glucose control, treatment requirement, maternal complications, fetal growth and surveillance.\n\nNICE advises elective birth between 37 weeks 0 days and 38 weeks 6 days for otherwise uncomplicated type 1 or type 2 diabetes.\n\nFor gestational diabetes, NICE advises birth no later than 40 weeks 6 days.\n\nCommon ACOG-based practice places well-controlled diet-treated gestational diabetes between 39 weeks and 40 weeks 6 days, medication-treated gestational diabetes in the 39th week, and well-controlled pre-existing diabetes without complications generally in the 39th week.\n\nEarlier birth may be required when control is poor or maternal–fetal complications develop. [3,4,8,10]" },
  { q: "Will my baby develop diabetes because I had diabetes in pregnancy?", a: "Not necessarily.\n\nThe child’s long-term metabolic health is influenced by genetics, pregnancy exposure, family nutrition, physical activity and many other factors.\n\nMaternal hyperglycaemia is associated with an increased later risk of obesity and abnormal glucose metabolism, but it does not determine the child’s future.\n\nImproving pregnancy glucose and supporting healthy family habits can reduce avoidable risk. [1,6]" },
  { q: "What happens to insulin immediately after birth?", a: "Placental delivery causes insulin resistance to fall rapidly.\n\nWomen with type 1 diabetes still require insulin, but the dose usually needs immediate reduction.\n\nWomen with insulin-treated type 2 diabetes also need prompt reassessment.\n\nWomen treated with insulin solely for gestational diabetes usually stop it after birth unless significant hyperglycaemia persists.\n\nThe postpartum treatment plan should be written before labour because continuing the full late-pregnancy insulin dose can cause severe hypoglycaemia. [1,4]" },
  { q: "What test do I need after gestational diabetes?", a: "ADA recommends a fasting 75 g oral glucose-tolerance test 4 to 12 weeks after birth, interpreted using non-pregnancy diagnostic criteria.\n\nThe OGTT is preferred over HbA1c at that stage because pregnancy-related red-cell changes and blood loss can distort HbA1c.\n\nEven when the postpartum result is normal, lifelong diabetes screening is recommended every 1 to 3 years. [1,2]" },
];

/* Inline citation marker → jumps to first cited reference. */
function Cite({ children }) {
  const m = String(children).match(/(\d+)/);
  const first = m ? m[1] : "";
  return (
    <sup className="text-prestige font-medium">
      <a href={`#ref-${first}`} className="hover:underline">
        [{children}]
      </a>
    </sup>
  );
}

/* Accordion row (all closed by default). Whitespace-pre-line preserves the
   paragraph and dash-list structure from the shared FAQ_ITEMS source. */
function FaqRow({ q, a }) {
  const [open, setOpen] = useState(false);
  const slug = q.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 50);
  const panelId = `faq-panel-${slug}`;
  return (
    <div className="border-t border-line last:border-b" data-testid={`faq-${slug}`}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start justify-between gap-6 py-5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-navy rounded-sm"
      >
        <span className="font-serif text-lg md:text-xl text-navy leading-snug pt-0.5">
          {q}
        </span>
        <ChevronDown
          size={20}
          strokeWidth={1.5}
          className={`text-prestige shrink-0 mt-1 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </button>
      {open && (
        <div id={panelId} role="region" aria-label={q} className="pb-6 pr-8 text-[15px] text-stone2 leading-relaxed max-w-3xl whitespace-pre-line">
          {a}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

export default function PregnancyWithDiabetes() {
  const faqJsonLd = FAQ_ITEMS.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQ_ITEMS.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : undefined;

  return (
    <div data-testid="pwd-page">
      <SEO
        title={PAGE_TITLE}
        description={PAGE_DESC}
        jsonLd={faqJsonLd}
        breadcrumbs={[
          { name: "Home", item: "/" },
          { name: "Pregnancy with Diabetes", item: "/pregnancy-with-diabetes" },
        ]}
      />

      {/* Hero */}
      <section className="bg-bone" data-testid="pwd-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="max-w-3xl">
            <Overline>Pregnancy with Diabetes</Overline>
            <div className="h-px w-12 bg-prestige my-6" />
            <h1
              data-testid="pwd-heading"
              className="font-serif italic text-4xl sm:text-5xl md:text-6xl leading-[1.05] text-prestige tracking-tight"
            >
              When Glucose Control Must Protect Both Mother and Baby
            </h1>
            <div className="text-[15px] text-stone2 mt-6 leading-relaxed max-w-2xl space-y-3">
              <p>Diabetes in pregnancy is not one condition.</p>
              <p>It may be type 1 diabetes requiring lifelong insulin.</p>
              <p>
                It may be type 2 diabetes that existed before conception but
                was recognised only after pregnancy began.
              </p>
              <p>
                It may be gestational diabetes caused by the increasing
                insulin resistance of pregnancy.
              </p>
              <p>
                It may occasionally represent a less common form of diabetes
                that requires a different approach.
              </p>
              <p>These conditions can produce similar glucose reports.</p>
              <p>They cannot always be treated in the same way.</p>
              <p>A woman may arrive with an HbA1c of 12% or 13%.</p>
              <p>
                Another may have a normal HbA1c but repeated high glucose
                values after meals.
              </p>
              <p>
                A third may already be using insulin without anyone having
                established clearly why it was started.
              </p>
              <p>
                The first responsibility is not to defend a particular
                medicine or diet.
              </p>
              <p>
                It is to identify the type of diabetes, reconstruct the
                glucose pattern and decide what the mother’s body requires
                now.
              </p>
              <p>Nutrition is the foundation of my diabetes care in pregnancy.</p>
              <p>
                The current glucose pattern shows whether that foundation is
                sufficient.
              </p>
              <p>
                Insulin is used, continued, reduced or withdrawn according to
                diabetes type, ketone risk, glucose response and maternal–
                fetal safety—not according to ideology.
              </p>
            </div>

            <div className="mt-7 border-l-2 border-prestige pl-5 max-w-xl">
              <p className="text-[15px] text-navy leading-relaxed">
                My approach begins with three principles:
              </p>
              <ul className="mt-3 space-y-2 text-[15px] text-navy leading-relaxed">
                <li>
                  <strong>First identify the type of diabetes.</strong>
                </li>
                <li>
                  <strong>
                    Treat the daily glucose pattern—not the HbA1c in isolation.
                  </strong>
                </li>
                <li>
                  <strong>
                    Use nutrition intensively while keeping every metabolic
                    safety threshold visible.
                  </strong>
                </li>
              </ul>
            </div>

            <div className="text-[15px] text-stone2 mt-6 leading-relaxed max-w-2xl space-y-3">
              <p>No doctor can promise a complication-free pregnancy.</p>
              <p>Expert care can do something more useful:</p>
            </div>
            <ul
              className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed max-w-2xl"
              role="list"
            >
              {[
                "Reduce avoidable glucose exposure.",
                "Identify dangerous metabolic patterns early.",
                "Protect adequate maternal nutrition.",
                "Detect when diet is working.",
                "Recognise when diet is not enough.",
                "Prevent unsafe interruption of essential insulin.",
                "Plan fetal surveillance and birth according to the complete pregnancy.",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <span
                    className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                    aria-hidden="true"
                  />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <div className="mt-9 flex flex-wrap gap-3">
              <PrimaryButton
                to="/contact?type=appointment&reason=diabetes"
                testid="pwd-hero-cta-primary"
              >
                Schedule a Pregnancy Diabetes Consultation
              </PrimaryButton>
              <OutlineButton
                to="#glucose-pattern"
                testid="pwd-hero-cta-secondary"
              >
                Understand Your Glucose Pattern
              </OutlineButton>
            </div>
          </div>
        </div>
      </section>

      {/* Two-column: contents menu (sticky on desktop) + main body */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-20">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Contents menu */}
          <aside
            data-testid="pwd-contents"
            className="lg:col-span-3 lg:sticky lg:top-32"
          >
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-prestige">
              On this page
            </div>
            <div className="h-px w-8 bg-prestige mt-3 mb-4" />
            <nav aria-label="Contents">
              <ul className="space-y-2.5">
                {TOC.map((t) => (
                  <li key={t.id}>
                    <a
                      href={`#${t.id}`}
                      data-testid={`toc-${t.id}`}
                      className="text-[14px] text-navy hover:text-prestige transition-colors leading-snug block"
                    >
                      {t.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>

          {/* Body */}
          <div className="lg:col-span-9 max-w-3xl">
            {/* --- PART 1 START ---------------------------------------- */}

            {/* First Identify the Type of Diabetes */}
            <section id="first-identify-the-type" className="scroll-mt-32">
              <Overline>Classification</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                First Identify the Type of Diabetes
              </h2>
              <div className="prose-rpl mt-5">
                <p>
                  Diabetes means that glucose remains in the blood at a level
                  that may affect maternal health, placental function or fetal
                  development.
                </p>
                <p>
                  The word <strong>diabetes</strong> does not explain why
                  this is happening.
                </p>
                <p>
                  That distinction matters because pregnancy management
                  depends on the underlying physiology.
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Type 1 Diabetes
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  Type 1 diabetes develops when the pancreas produces little
                  or no insulin, most commonly because the insulin-producing
                  beta cells have been destroyed by an autoimmune process.
                </p>
                <p>A woman with type 1 diabetes requires insulin.</p>
                <p>
                  Nutrition, exercise, glucose monitoring and technology can
                  improve control dramatically.
                </p>
                <p>
                  They cannot replace the insulin that her body is unable to
                  produce.
                </p>
                <p>
                  Stopping insulin in type 1 diabetes can lead to diabetic
                  ketoacidosis, a medical emergency for both mother and baby.
                  <Cite>1,4</Cite>
                </p>
                <p>
                  Type 1 diabetes may already be well established before
                  pregnancy.
                </p>
                <p>
                  Occasionally, it is first recognised during pregnancy after
                  symptoms, marked hyperglycaemia or ketosis.
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Type 2 Diabetes
              </h3>
              <div className="prose-rpl mt-4">
                <p>Type 2 diabetes usually combines:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Insulin resistance.",
                  "Inadequate insulin production for the body’s needs.",
                  "A progressive loss of beta-cell function over time.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>It may be known before conception.</p>
                <p>
                  It may also be discovered for the first time during
                  pregnancy.
                </p>
                <p>The treatment requirement varies widely.</p>
                <p>
                  Some women have substantial insulin resistance but retain
                  enough pancreatic reserve to respond strongly to structured
                  nutrition, meal timing, activity and weight-appropriate
                  care.
                </p>
                <p>
                  Others require medication or insulin to reach pregnancy
                  glucose targets.
                </p>
                <p>
                  A high HbA1c does not by itself reveal how much pancreatic
                  reserve remains.
                </p>
                <p>
                  The current glucose pattern and response to closely
                  supervised treatment provide additional information.
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Gestational Diabetes
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  Gestational diabetes is diabetes diagnosed during pregnancy
                  that was not clearly present before pregnancy.
                </p>
                <p>
                  It usually becomes apparent in the second or third
                  trimester, when placental hormones create increasing
                  insulin resistance.
                </p>
                <p>
                  Gestational diabetes is not proof that a woman ate badly.
                </p>
                <p>
                  Pregnancy often reveals a metabolic vulnerability that was
                  previously silent.
                </p>
                <p>
                  Many women with gestational diabetes achieve target glucose
                  levels through individualised nutrition and appropriate
                  physical activity.
                </p>
                <p>
                  Some require medication or insulin because the
                  placenta-driven insulin resistance exceeds what their
                  pancreas can overcome.<Cite>1,3,6</Cite>
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Diabetes First Recognised Early in Pregnancy
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  Marked hyperglycaemia found at the booking visit or early
                  in pregnancy may represent previously undiagnosed type 1
                  or type 2 diabetes rather than conventional gestational
                  diabetes.
                </p>
                <p>Clues include:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "An HbA1c already in the diabetes range.",
                  "Fasting glucose in the diabetes range.",
                  "Classic symptoms of hyperglycaemia.",
                  "Unexplained weight loss.",
                  "Ketones.",
                  "A previous history of gestational diabetes.",
                  "Previous high glucose values outside pregnancy.",
                  "Polycystic ovary syndrome or significant insulin resistance.",
                  "A strong family history.",
                  "Diabetes-related eye, kidney, nerve or vascular disease.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>The label matters.</p>
                <p>
                  Pre-existing diabetes carries implications for early fetal
                  development and maternal complication screening that are
                  different from diabetes beginning later in pregnancy.
                  <Cite>1,2,4</Cite>
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Less Common Forms of Diabetes
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  Not every case fits neatly into type 1, type 2 or
                  gestational diabetes.
                </p>
                <p>Selected women may have:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Monogenic diabetes.",
                  "Pancreatic disease.",
                  "Steroid-related hyperglycaemia.",
                  "Medication-related diabetes.",
                  "Endocrine disease affecting glucose.",
                  "Ketosis-prone diabetes.",
                  "Autoimmune diabetes developing more slowly in adulthood.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>
                  These possibilities become more relevant when the history,
                  body type, family pattern, ketones or treatment response
                  do not fit the presumed diagnosis.
                </p>
                <p>
                  They require selective investigation—not indiscriminate
                  testing for every patient.
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Insulin Use Does Not Define the Diabetes Type
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  Being prescribed insulin does not prove that a woman has
                  type 1 diabetes.
                </p>
                <p>
                  Not using insulin does not prove that she has type 2
                  diabetes.
                </p>
                <p>Insulin may have been started because:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Glucose was extremely high.",
                  "Pregnancy targets were not being reached.",
                  "Oral medicines were stopped.",
                  "The clinician required rapid control.",
                  "The diabetes type had not yet been clarified.",
                  "The woman was acutely unwell.",
                  "Steroids or infection temporarily raised glucose.",
                  "Placental insulin resistance increased later in pregnancy.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">
                The indication for insulin should therefore be reconstructed
                rather than assumed.
              </p>

              <div className="mt-6 bg-surfacealt/60 border-l-2 border-prestige p-6">
                <p className="text-[15px] text-navy leading-relaxed">
                  <strong>
                    The treatment a woman arrived with is part of the
                    history. It is not automatically the final diagnosis.
                  </strong>
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Why Classification Changes Every Decision
              </h3>
              <div className="prose-rpl mt-4">
                <p>The diabetes type influences:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Whether insulin is essential for survival.",
                  "Whether ketone monitoring is required.",
                  "Whether medication can be safely reduced.",
                  "Which glucose technology may help.",
                  "Which maternal complications must be assessed.",
                  "How early fetal effects may have occurred.",
                  "How fetal growth is interpreted.",
                  "How quickly treatment must be intensified.",
                  "What happens to treatment immediately after birth.",
                  "The mother’s long-term follow-up.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-6 bg-surfacealt/60 border-l-2 border-prestige p-6">
                <p className="text-[15px] text-navy leading-relaxed">
                  <strong>
                    Before asking, “Can insulin be stopped?” first ask, “What
                    form of diabetes are we treating?”
                  </strong>
                </p>
              </div>
            </section>

            {/* HbA1c Is a Risk Marker, Not a Treatment Plan */}
            <section id="hba1c-risk-marker" className="scroll-mt-32 mt-16">
              <Overline>Interpreting HbA1c</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                HbA1c Is a Risk Marker, Not a Treatment Plan
              </h2>
              <div className="prose-rpl mt-5">
                <p>
                  HbA1c estimates average glucose exposure over the preceding
                  weeks.
                </p>
                <p>
                  It is weighted toward more recent glucose levels, but it
                  does not show:
                </p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "The fasting value.",
                  "The post-meal rise.",
                  "Overnight glucose.",
                  "Glucose variability.",
                  "Hypoglycaemia.",
                  "Ketones.",
                  "The effect of individual foods.",
                  "Whether current control has already improved.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">
                It is therefore valuable—but incomplete.
              </p>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Why HbA1c Around Conception Matters
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  The baby’s major organs begin developing very early.
                </p>
                <p>
                  In pre-existing diabetes, the risk of congenital anomalies
                  rises progressively as glucose exposure around conception
                  and during early organ development increases.
                </p>
                <p>The relationship is continuous.</p>
                <p>
                  There is no single HbA1c value below which every baby is
                  guaranteed to be unaffected.
                </p>
                <p>
                  There is no single value above which an adverse outcome is
                  certain.
                </p>
                <p>
                  Preconception guidelines aim for an HbA1c below 6.5% when
                  this can be achieved without problematic hypoglycaemia.
                </p>
                <p>
                  NICE advises women with an HbA1c above 10% to delay
                  conception until control has improved because the risk is
                  substantially increased.<Cite>1,4</Cite>
                </p>
                <p>
                  If the woman is already pregnant, a very high HbA1c is not
                  a reason for blame, panic or abandonment.
                </p>
                <p>It is a reason for:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Prompt specialist assessment.",
                  "Immediate review of current glucose.",
                  "Clarification of diabetes type.",
                  "Ketone assessment when indicated.",
                  "Safe and decisive treatment.",
                  "Review for maternal complications.",
                  "Appropriate fetal assessment later in pregnancy.",
                  "Honest counselling without false certainty.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                What an HbA1c of 12% or 13% Means
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  An HbA1c of 12% or 13% indicates severe recent
                  hyperglycaemia.
                </p>
                <p>
                  It tells us that glucose exposure has been high over time.
                </p>
                <p>It does not tell us:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Whether the woman has type 1 or type 2 diabetes.",
                  "Whether she is producing insulin.",
                  "Whether ketones are present.",
                  "Whether glucose has already improved during the last several days.",
                  "Which meals are producing the largest rises.",
                  "Whether insulin can be reduced safely.",
                  "Whether insulin must be continued.",
                  "Whether every fetal complication will occur.",
                  "Whether the pregnancy cannot have a good outcome.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>The number should trigger urgency.</p>
                <p>It should not replace clinical reasoning.</p>
              </div>

              <div className="mt-6 bg-surfacealt/60 border-l-2 border-prestige p-6">
                <p className="text-[15px] text-navy leading-relaxed">
                  <strong>
                    A very high HbA1c tells us that the pregnancy needs
                    rapid, organised care. It does not tell us that every
                    woman needs the same treatment.
                  </strong>
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                High HbA1c Does Not Mean “Insulin Forever”
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  Some women with previously unrecognised type 2 diabetes
                  present with very high HbA1c levels because their food
                  pattern, meal timing, glucose load and insulin resistance
                  have remained unaddressed for months.
                </p>
                <p>
                  With intensive nutrition, frequent glucose monitoring and
                  correction of the daily pattern, their glucose may improve
                  dramatically.
                </p>
                <p>
                  Selected women may be able to reduce medication or insulin
                  under close supervision.
                </p>
                <p>
                  This possibility should be assessed from the actual
                  response—not promised from the starting HbA1c.
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                High HbA1c Does Not Mean “Stop Insulin Immediately”
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  A high HbA1c also does not prove that insulin is
                  unnecessary.
                </p>
                <p>
                  Insulin should not be withdrawn merely because nutrition
                  is being introduced.
                </p>
                <p>
                  Before reducing or stopping insulin, the clinician must
                  consider:
                </p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "The confirmed or suspected diabetes type.",
                  "Current fasting and post-meal values.",
                  "Symptoms of insulin deficiency.",
                  "Blood or urine ketones.",
                  "Recent weight loss.",
                  "Illness or vomiting.",
                  "The existing insulin dose and pattern.",
                  "Hypoglycaemia.",
                  "Renal and liver function.",
                  "The speed and consistency of response to nutrition.",
                  "Gestational age and changing placental insulin resistance.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>In type 1 diabetes, insulin must continue.</p>
                <p>
                  In type 2 or gestational diabetes, the requirement may
                  change substantially and should be adjusted according to
                  monitored response.<Cite>1,3,4</Cite>
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                HbA1c Can Be Distorted
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  HbA1c may not reflect glucose accurately when
                  red-blood-cell turnover or haemoglobin is altered.
                </p>
                <p>Interpretation may be affected by:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Pregnancy itself.",
                  "Iron-deficiency anaemia.",
                  "Recent blood loss.",
                  "Blood transfusion.",
                  "Haemoglobin variants.",
                  "Haemolysis.",
                  "Kidney disease.",
                  "Some treatments affecting red-cell production.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">
                An HbA1c that conflicts with the glucose record should be
                questioned rather than obeyed blindly.<Cite>1,2</Cite>
              </p>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                HbA1c Becomes a Secondary Measure During Pregnancy
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  During pregnancy, HbA1c is useful for estimating broad
                  recent exposure and tracking direction.
                </p>
                <p>
                  Daily management depends more directly on glucose readings.
                </p>
                <p>
                  Post-meal glucose is particularly important because a
                  satisfactory average can conceal repeated meal-related
                  peaks.
                </p>
                <p>
                  A woman can have the same HbA1c as another woman while
                  having a completely different glucose pattern and treatment
                  need.
                </p>
              </div>

              <div className="mt-6 bg-surfacealt/60 border-l-2 border-prestige p-6">
                <p className="text-[15px] text-navy leading-relaxed">
                  <strong>
                    HbA1c describes the recent past. Today’s glucose pattern
                    determines today’s action.
                  </strong>
                </p>
              </div>
            </section>

            {/* The Glucose Pattern Matters More Than One Number */}
            <section id="glucose-pattern" className="scroll-mt-32 mt-16">
              <Overline>Daily Glucose Pattern</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                The Glucose Pattern Matters More Than One Number
              </h2>
              <div className="prose-rpl mt-5">
                <p>
                  A single fasting result, a single post-meal result or one
                  HbA1c cannot describe the whole metabolic day.
                </p>
                <p>The useful question is:</p>
                <p>
                  <strong>
                    When is the glucose rising, how high is it rising, and
                    what is driving the rise?
                  </strong>
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Fasting Glucose
              </h3>
              <div className="prose-rpl mt-4">
                <p>Fasting glucose may reflect:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Overnight liver glucose production.",
                  "The previous evening meal.",
                  "A late-night snack.",
                  "Prolonged fasting.",
                  "Poor sleep.",
                  "Stress hormones.",
                  "Inadequate overnight insulin.",
                  "Excessive overnight insulin followed by counter-regulation.",
                  "Infection or illness.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">
                It should not automatically be blamed on the last food eaten.
              </p>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Post-Meal Glucose
              </h3>
              <div className="prose-rpl mt-4">
                <p>Post-meal glucose may be influenced by:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "The amount and type of carbohydrate.",
                  "Protein and fibre accompanying the meal.",
                  "Fat content and delayed absorption.",
                  "Portion size.",
                  "Meal timing.",
                  "Physical activity after eating.",
                  "Insulin timing.",
                  "Gastric emptying.",
                  "Sleep and stress.",
                  "Gestational age.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>The same food may produce different results in two women.</p>
                <p>The same woman may respond differently as pregnancy advances.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Overnight Glucose
              </h3>
              <div className="prose-rpl mt-4">
                <p>Overnight information can reveal:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Unrecognised hypoglycaemia.",
                  "Persistent hyperglycaemia.",
                  "A dawn rise.",
                  "The effect of a late meal.",
                  "The effect of basal insulin.",
                  "Long fasting intervals.",
                  "Glucose instability that an HbA1c conceals.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>
                  Continuous glucose monitoring may be particularly useful in
                  type 1 diabetes and selected insulin-treated pregnancies.
                </p>
                <p>
                  Finger-prick monitoring remains important when sensor
                  readings do not match symptoms or when treatment decisions
                  require confirmation.<Cite>1,4</Cite>
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Glucose Variability Matters
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  Repeated swings between high and low glucose can coexist
                  with an apparently acceptable average.
                </p>
                <p>The objective is not merely to create a better HbA1c.</p>
                <p>It is to build a safer daily pattern with:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Fewer high post-meal peaks.",
                  "Fewer episodes of hypoglycaemia.",
                  "Adequate maternal food intake.",
                  "Appropriate fetal growth.",
                  "Treatment that the woman can sustain.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Two Women With the Same HbA1c May Need Different Plans
              </h3>

              <h4 className="font-serif text-xl text-navy tracking-tight leading-snug mt-8">
                Pattern One
              </h4>
              <div className="prose-rpl mt-4">
                <p>
                  A woman has a high HbA1c, high fasting glucose and high
                  readings throughout the day.
                </p>
                <p>
                  This suggests sustained hyperglycaemia and may require
                  rapid treatment intensification.
                </p>
              </div>

              <h4 className="font-serif text-xl text-navy tracking-tight leading-snug mt-8">
                Pattern Two
              </h4>
              <div className="prose-rpl mt-4">
                <p>
                  A woman has the same HbA1c but current fasting values are
                  improving and the remaining rises occur after two
                  identifiable meals.
                </p>
                <p>
                  Her treatment should focus on the residual pattern rather
                  than the historical average alone.
                </p>
              </div>

              <h4 className="font-serif text-xl text-navy tracking-tight leading-snug mt-8">
                Pattern Three
              </h4>
              <div className="prose-rpl mt-4">
                <p>
                  A woman has an acceptable HbA1c but recurrent post-meal
                  peaks and episodes of hypoglycaemia between meals.
                </p>
                <p>Her average is concealing instability.</p>
              </div>

              <h4 className="font-serif text-xl text-navy tracking-tight leading-snug mt-8">
                Pattern Four
              </h4>
              <div className="prose-rpl mt-4">
                <p>
                  A woman has high glucose, weight loss, vomiting or ketones.
                </p>
                <p>
                  Her immediate metabolic safety takes priority over gradual
                  dietary experimentation.
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                The First Few Days of Structured Monitoring Are Highly Informative
              </h3>
              <div className="prose-rpl mt-4">
                <p>A carefully recorded glucose pattern can show:</p>
              </div>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Whether nutrition is producing a rapid response.",
                  "Whether fasting or post-meal glucose is the main problem.",
                  "Whether medication is causing hypoglycaemia.",
                  "Whether insulin needs to be continued, reduced or intensified.",
                  "Whether the presumed diabetes type fits the response.",
                  "Whether urgent reassessment is required.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">
                The record should include:
              </p>
              <ul
                className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed"
                role="list"
              >
                {[
                  "Time of glucose testing.",
                  "Relation to the meal.",
                  "Meal content.",
                  "Medicine or insulin timing.",
                  "Symptoms.",
                  "Physical activity.",
                  "Vomiting or illness.",
                  "Ketones when indicated.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span
                      className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-6 bg-surfacealt/60 border-l-2 border-prestige p-6">
                <p className="text-[15px] text-navy leading-relaxed">
                  <strong>
                    Treatment becomes safer when every glucose value is
                    placed back into its clinical context.
                  </strong>
                </p>
              </div>

              <p className="mt-8 text-[15px] text-stone2 leading-relaxed">
                The next step is to reconstruct the complete history before
                ordering or interpreting further investigations.
              </p>
            </section>

            {/* --- PART 1 END --------------------------------------- */}

            {/* --- PART 2 START ------------------------------------- */}

            {/* Reconstructing the Story */}
            <section id="reconstructing-the-story" className="scroll-mt-32 mt-16">
              <Overline>The First Consultation</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                Reconstructing the Story
              </h2>
              <div className="prose-rpl mt-5">
                <p>A glucose report is only one part of the consultation.</p>
                <p>
                  Before changing food, tablets or insulin, the clinical
                  story should establish:
                </p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "What type of diabetes is most likely.",
                  "When abnormal glucose first appeared.",
                  "Whether the woman is metabolically stable.",
                  "What treatment she is currently using.",
                  "Whether current readings differ from the HbA1c.",
                  "Whether pregnancy complications or diabetic complications are already present.",
                  "What the woman is actually eating, tolerating and able to sustain.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                The Diabetes Timeline
              </h3>
              <div className="prose-rpl mt-4">
                <p>I review:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Age at diabetes diagnosis.",
                  "Whether diagnosis occurred before or during pregnancy.",
                  "Symptoms present at diagnosis.",
                  "The original fasting and post-meal glucose values.",
                  "The original HbA1c.",
                  "Whether ketones or acidosis were present.",
                  "Whether hospital admission was required.",
                  "Whether antibodies, C-peptide or another classification test was performed.",
                  "Every medicine or insulin preparation previously used.",
                  "Why each treatment was started.",
                  "Why any treatment was stopped.",
                  "Previous periods of control without medication.",
                  "Previous severe hyperglycaemia.",
                  "Previous diabetic ketoacidosis.",
                  "Previous severe hypoglycaemia.",
                  "Impaired awareness of hypoglycaemia.",
                  "Use of an insulin pump or continuous glucose monitor.",
                  "Diabetes education received previously.",
                  "Access to emergency advice and supplies.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>The objective is not to produce a longer label.</p>
                <p>
                  It is to determine whether the presumed diagnosis fits the
                  woman’s physiology and treatment response.
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                The Current Pregnancy Timeline
              </h3>
              <div className="prose-rpl mt-4">
                <p>I review:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Gestational age.",
                  "Whether the pregnancy was planned.",
                  "Glucose control around conception.",
                  "The earliest HbA1c in the pregnancy.",
                  "Current fasting, pre-meal and post-meal values.",
                  "Nausea, vomiting or reduced food intake.",
                  "Weight before pregnancy.",
                  "Current weight and weight change.",
                  "Symptoms of hyperglycaemia.",
                  "Symptoms of hypoglycaemia.",
                  "Infection, fever or steroid exposure.",
                  "Blood-pressure readings.",
                  "Urine protein.",
                  "Vaginal bleeding or threatened pregnancy loss.",
                  "Fetal growth and ultrasound findings already available.",
                  "Current fetal-movement pattern when gestational age is appropriate.",
                  "Singleton or multiple pregnancy.",
                  "IVF or assisted-conception history.",
                  "Other maternal medical conditions.",
                  "Every prescribed medicine, supplement, powder and herbal preparation.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>Pregnancy changes insulin sensitivity over time.</p>
                <p>
                  A plan that worked four weeks earlier may no longer match
                  the current physiology.
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Previous Pregnancies Matter
              </h3>
              <div className="prose-rpl mt-4">
                <p>I ask about:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Previous gestational diabetes.",
                  "Previous type 2 diabetes diagnosed after pregnancy.",
                  "Previous miscarriage.",
                  "Previous congenital anomaly.",
                  "Previous stillbirth.",
                  "Previous pre-eclampsia.",
                  "Previous preterm birth.",
                  "Previous polyhydramnios.",
                  "Previous fetal growth restriction.",
                  "Previous large-for-gestational-age baby.",
                  "Previous shoulder dystocia or birth injury.",
                  "Previous caesarean birth.",
                  "Previous newborn hypoglycaemia.",
                  "Previous neonatal-unit admission.",
                  "The mother’s treatment during each pregnancy.",
                  "Whether glucose returned to normal after birth.",
                  "Whether postpartum diabetes testing was completed.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>A previous outcome does not guarantee repetition.</p>
                <p>
                  It can reveal where the next pregnancy requires earlier
                  prevention.
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                The Nutrition and Meal-Timing History
              </h3>
              <div className="prose-rpl mt-4">
                <p>Before prescribing a diet, I review:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Usual wake and sleep times.",
                  "Time of the first meal.",
                  "Length of the overnight fast.",
                  "Number and timing of meals.",
                  "Long gaps without food.",
                  "Large evening meals.",
                  "Bedtime eating.",
                  "Vegetarian or non-vegetarian pattern.",
                  "Sources of protein.",
                  "Type and quantity of cereal, flour, rice and other starch.",
                  "Fruit quantity and timing.",
                  "Milk and dairy intake.",
                  "Sweetened drinks.",
                  "Added sugar, jaggery, honey and syrups.",
                  "Fried and ultra-processed foods.",
                  "Restaurant and takeaway meals.",
                  "Fibre intake.",
                  "Hydration.",
                  "Food aversions.",
                  "Nausea, vomiting, reflux or constipation.",
                  "Food insecurity or financial limitations.",
                  "Religious fasting.",
                  "Work shifts and travel.",
                  "Physical activity before and after meals.",
                  "Foods repeatedly associated with glucose rises.",
                  "Foods removed unnecessarily after diagnosis.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>The purpose is not to search for one “bad” food.</p>
                <p>
                  It is to understand the complete metabolic pattern and
                  preserve adequate pregnancy nutrition.
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                The Insulin History
              </h3>
              <div className="prose-rpl mt-4">
                <p>When insulin is already being used, I review:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "The exact insulin names.",
                  "Basal, mealtime, mixed or pump regimen.",
                  "Dose and timing.",
                  "Injection technique.",
                  "Needle length.",
                  "Injection sites.",
                  "Rotation of sites.",
                  "Storage.",
                  "Missed or delayed doses.",
                  "Timing in relation to food.",
                  "Correction doses.",
                  "Insulin-to-carbohydrate ratios when used.",
                  "Pump settings when relevant.",
                  "Episodes of nocturnal hypoglycaemia.",
                  "Rebound eating after hypoglycaemia.",
                  "Whether doses were increased without reviewing food intake.",
                  "Whether glucose fell rapidly after dietary change.",
                  "Whether the woman is afraid to reduce or take insulin.",
                  "Whether she has glucagon and knows how it is used.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>An insulin prescription can fail because the dose is wrong.</p>
                <p>
                  It can also fail because the timing, technique, meal
                  pattern or diagnosis is wrong.
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Symptoms That Require Immediate Metabolic Assessment
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  Urgent assessment is required when diabetes in pregnancy
                  is accompanied by:
                </p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Persistent vomiting.",
                  "Abdominal pain.",
                  "Rapid or deep breathing.",
                  "Marked thirst.",
                  "Frequent urination with dehydration.",
                  "Unexplained weight loss.",
                  "Severe weakness.",
                  "Drowsiness or confusion.",
                  "Fruity-smelling breath.",
                  "Moderate or high ketones.",
                  "Persistent glucose elevation during illness.",
                  "Inability to keep food or fluids down.",
                  "Recurrent severe hypoglycaemia.",
                  "Loss of consciousness.",
                  "Seizure.",
                  "Reduced fetal movements.",
                  "A strong feeling that something is seriously wrong.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>Pregnancy increases susceptibility to ketosis.</p>
                <p>
                  Diabetic ketoacidosis can occur at lower glucose levels
                  than many patients expect.
                </p>
                <p>
                  A woman should not wait for glucose to become “extremely
                  high” before seeking help when she is unwell or ketones
                  are present.<Cite>1,4,7</Cite>
                </p>
              </div>

              <div className="mt-6 bg-surfacealt/60 border-l-2 border-prestige p-6">
                <p className="text-[15px] text-navy leading-relaxed">
                  <strong>
                    In pregnancy, vomiting plus diabetes is a metabolic
                    question—not merely a digestive symptom.
                  </strong>
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                What I Ask Patients to Bring
              </h3>
              <div className="prose-rpl mt-4">
                <p>Please bring every available:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Glucose log.",
                  "Glucose-meter download.",
                  "Continuous-glucose-monitor report.",
                  "Insulin-pump report.",
                  "HbA1c report.",
                  "Oral glucose-tolerance-test report.",
                  "Fasting and post-meal laboratory reports.",
                  "Ketone results.",
                  "Hospital records from hyperglycaemia, ketoacidosis or hypoglycaemia.",
                  "Previous diabetes-classification tests.",
                  "Kidney-function and urine-protein reports.",
                  "Retinal-screening report.",
                  "Thyroid reports.",
                  "Blood-pressure record.",
                  "Ultrasound and fetal-growth reports.",
                  "Previous pregnancy and delivery summaries.",
                  "Neonatal discharge summaries.",
                  "Complete medicine and supplement list.",
                  "Insulin pens, meter and strips when technique needs review.",
                  "A three-day food, meal-timing, activity and glucose record when possible.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>The record is not used to grade obedience.</p>
                <p>It is used to identify patterns that can be changed safely.</p>
                <p>Missing documents should never prevent consultation.</p>
                <p>
                  The timeline can still be reconstructed from the
                  information available.
                </p>
              </div>

              <div className="mt-6 bg-surfacealt/60 border-l-2 border-prestige p-6">
                <p className="text-[15px] text-navy leading-relaxed">
                  <strong>
                    One glucose value describes a moment. A linked record of
                    food, treatment, activity and symptoms explains the
                    moment.
                  </strong>
                </p>
              </div>
            </section>

            {/* The Investigation Map */}
            <section id="investigation-map" className="scroll-mt-32 mt-16">
              <Overline>Investigations</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                The Investigation Map
              </h2>
              <div className="prose-rpl mt-5">
                <p>Testing should answer a clinical question.</p>
                <p>
                  It should not become a standard shopping list given to
                  every woman.
                </p>
                <p>The investigation plan depends on:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "The presumed diabetes type.",
                  "Gestational age.",
                  "Severity of hyperglycaemia.",
                  "Ketone risk.",
                  "Existing treatment.",
                  "Previous diabetic complications.",
                  "Current maternal symptoms.",
                  "The fetal picture.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                1. Confirming Hyperglycaemia and Diabetes Type
              </h3>
              <div className="prose-rpl mt-4">
                <p>Useful evidence may include:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Laboratory fasting plasma glucose.",
                  "Random plasma glucose when symptoms are present.",
                  "HbA1c.",
                  "A diagnostic oral glucose-tolerance test when appropriate.",
                  "Previous results outside pregnancy.",
                  "Current home-glucose profile.",
                  "Ketones.",
                  "Selected C-peptide or diabetes-autoantibody testing when classification remains uncertain.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>
                  C-peptide and antibody testing are not routine
                  gestational-diabetes tests.
                </p>
                <p>
                  They may be useful when the presentation does not fit the
                  presumed diagnosis—for example:
                </p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Lean body habitus with marked hyperglycaemia.",
                  "Ketosis.",
                  "Rapid insulin requirement.",
                  "Strong multigenerational diabetes pattern.",
                  "Unexpected ability or inability to stop insulin.",
                  "Diabetes diagnosed at an unusual age.",
                  "A suspected monogenic form.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                2. Oral Glucose-Tolerance Testing
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  Different countries and professional bodies use different
                  diagnostic pathways for gestational diabetes.
                </p>
              </div>

              <h4 className="font-serif text-xl text-navy tracking-tight leading-snug mt-8">
                One-Step 75 g OGTT
              </h4>
              <div className="prose-rpl mt-4">
                <p>
                  The ADA-recognised one-step approach diagnoses gestational
                  diabetes when <strong>one or more</strong> of the following
                  plasma-glucose values are met or exceeded:
                </p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  { text: "Fasting: ", strong: "92 mg/dL", mid: " or ", strong2: "5.1 mmol/L" },
                  { text: "One hour: ", strong: "180 mg/dL", mid: " or ", strong2: "10.0 mmol/L" },
                  { text: "Two hours: ", strong: "153 mg/dL", mid: " or ", strong2: "8.5 mmol/L", cite: "2" },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>
                      {item.text}
                      <strong>{item.strong}</strong>
                      {item.mid}
                      <strong>{item.strong2}</strong>
                      {item.cite && <> <Cite>{item.cite}</Cite></>}
                    </span>
                  </li>
                ))}
              </ul>

              <h4 className="font-serif text-xl text-navy tracking-tight leading-snug mt-8">
                Two-Step Approach
              </h4>
              <div className="prose-rpl mt-4">
                <p>
                  The two-step approach begins with a non-fasting 50 g
                  glucose challenge.
                </p>
                <p>
                  Women whose screening result reaches the selected threshold
                  proceed to a fasting 100 g OGTT.
                </p>
                <p>
                  Using Carpenter–Coustan criteria, gestational diabetes is
                  generally diagnosed when at least two values reach or
                  exceed:
                </p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  { text: "Fasting: ", strong: "95 mg/dL", mid: " or ", strong2: "5.3 mmol/L" },
                  { text: "One hour: ", strong: "180 mg/dL", mid: " or ", strong2: "10.0 mmol/L" },
                  { text: "Two hours: ", strong: "155 mg/dL", mid: " or ", strong2: "8.6 mmol/L" },
                  { text: "Three hours: ", strong: "140 mg/dL", mid: " or ", strong2: "7.8 mmol/L", cite: "2,3" },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>
                      {item.text}
                      <strong>{item.strong}</strong>
                      {item.mid}
                      <strong>{item.strong2}</strong>
                      {item.cite && <> <Cite>{item.cite}</Cite></>}
                    </span>
                  </li>
                ))}
              </ul>

              <h4 className="font-serif text-xl text-navy tracking-tight leading-snug mt-8">
                NICE 75 g OGTT
              </h4>
              <div className="prose-rpl mt-4">
                <p>
                  NICE diagnoses gestational diabetes when either of the
                  following is present:
                </p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  { text: "Fasting: ", strong: "101 mg/dL", mid: " or ", strong2: "5.6 mmol/L" },
                  { text: "Two hours: ", strong: "140 mg/dL", mid: " or ", strong2: "7.8 mmol/L", cite: "4" },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>
                      {item.text}
                      <strong>{item.strong}</strong>
                      {item.mid}
                      <strong>{item.strong2}</strong>
                      {item.cite && <> <Cite>{item.cite}</Cite></>}
                    </span>
                  </li>
                ))}
              </ul>

              <div className="prose-rpl mt-6">
                <p>These thresholds are not interchangeable.</p>
                <p>A report should be interpreted according to:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "The glucose load used.",
                  "Whether the woman was fasting.",
                  "The sampling times.",
                  "The diagnostic system being followed.",
                  "Whether diabetes was already evident before the test.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-6 bg-surfacealt/60 border-l-2 border-prestige p-6">
                <p className="text-[15px] text-navy leading-relaxed">
                  <strong>
                    A woman should not be told that her OGTT is normal or
                    abnormal until the correct criteria have been applied to
                    the actual test she underwent.
                  </strong>
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                3. HbA1c
              </h3>
              <div className="prose-rpl mt-4">
                <p>HbA1c helps estimate recent glucose exposure.</p>
                <p>
                  In pre-existing diabetes, it also contributes to
                  early-pregnancy risk counselling.
                </p>
                <p>It should not replace:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Fasting glucose.",
                  "Post-meal monitoring.",
                  "Ketone assessment.",
                  "Recognition of hypoglycaemia.",
                  "Direct review of continuous-glucose-monitor data.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">
                When HbA1c and glucose readings disagree substantially,
                possible assay or red-cell interference should be considered.
                <Cite>1,2</Cite>
              </p>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                4. Ketones and Acid–Base Assessment
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  Blood ketones are preferable when diabetic ketoacidosis is
                  suspected because they assess beta-hydroxybutyrate more
                  directly than urine testing.
                </p>
                <p>
                  Urgent ketone assessment is appropriate when a pregnant
                  woman with any form of diabetes is:
                </p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Hyperglycaemic and unwell.",
                  "Vomiting.",
                  "Unable to eat.",
                  "Dehydrated.",
                  "Losing weight unexpectedly.",
                  "Experiencing abdominal pain.",
                  "Breathing rapidly or deeply.",
                  "Using an insulin pump with unexplained high glucose.",
                  "Suspected of missing insulin.",
                  "Taking a medicine associated with ketoacidosis.",
                  "Showing persistent glucose elevation during infection.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">
                Further emergency assessment may include:
              </p>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Electrolytes.",
                  "Bicarbonate.",
                  "Venous or arterial pH.",
                  "Renal function.",
                  "Anion gap.",
                  "Blood count.",
                  "Infection assessment.",
                  "Maternal and fetal monitoring according to gestational age.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>Diabetic ketoacidosis is an emergency.</p>
                <p>
                  It should not be managed through website instructions or
                  routine messaging.<Cite>1,4,7</Cite>
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                5. Kidney Assessment
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  For women with pre-existing diabetes, assessment commonly
                  includes:
                </p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Serum creatinine.",
                  "Urinary albumin-to-creatinine ratio or another appropriate protein assessment.",
                  "Blood pressure.",
                  "Review of previous diabetic kidney disease.",
                  "Review of medicines that may require alteration in pregnancy.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>
                  Estimated glomerular filtration rate is not considered a
                  reliable measure of kidney function during pregnancy.
                </p>
                <p>
                  Significant creatinine elevation or proteinuria may require
                  nephrology and maternal–fetal-medicine input.<Cite>1,4</Cite>
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                6. Retinal Assessment
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  Pregnancy and rapid improvement in glucose can affect
                  diabetic retinopathy.
                </p>
                <p>
                  Women with pre-existing type 1 or type 2 diabetes require
                  retinal assessment according to their previous screening
                  and current findings.
                </p>
                <p>
                  NICE advises retinal imaging after the first antenatal
                  appointment unless assessment occurred within the preceding
                  three months, with further assessment later in pregnancy
                  according to baseline findings.
                </p>
                <p>
                  Gestational diabetes alone does not require
                  diabetic-retinopathy screening because it has not been
                  present long enough to cause pregnancy-related progression
                  of established diabetic eye disease.<Cite>1,4</Cite>
                </p>
                <p>
                  The presence of retinopathy is not a reason to leave severe
                  hyperglycaemia untreated.
                </p>
                <p>
                  It is a reason to coordinate rapid glucose improvement with
                  appropriate ophthalmic follow-up.
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                7. Blood Pressure and Proteinuria
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  Diabetes increases the risk of hypertensive disease in
                  pregnancy.
                </p>
                <p>Assessment includes:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Correctly measured blood pressure.",
                  "Urine protein when indicated.",
                  "Baseline kidney status.",
                  "Symptoms of pre-eclampsia.",
                  "Platelets, liver tests and other investigations when clinically required.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>
                  Protein present early in pregnancy may reflect pre-existing
                  diabetic kidney disease.
                </p>
                <p>
                  Protein appearing later may represent pre-eclampsia, kidney
                  disease or both.
                </p>
                <p>The distinction may require specialist assessment.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                8. Thyroid Assessment
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  Autoimmune thyroid disease is more common in women with
                  type 1 diabetes.
                </p>
                <p>Thyroid testing may be indicated according to:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Diabetes type.",
                  "Previous thyroid disease.",
                  "Thyroid antibodies.",
                  "Symptoms.",
                  "Goitre.",
                  "Previous abnormal results.",
                  "Local pregnancy-screening policy.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>Thyroid tests are not a substitute for diabetes classification.</p>
                <p>
                  They assess an associated condition that may also affect
                  pregnancy.
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                9. Liver, Lipid and Medication Review
              </h3>
              <div className="prose-rpl mt-4">
                <p>Selected women may require:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Liver-function tests.",
                  "Assessment for metabolic fatty-liver disease.",
                  "Review of previous lipid results.",
                  "Electrolytes.",
                  "Vitamin B12 assessment after prolonged metformin exposure when clinically indicated.",
                  "Review of antihypertensive medicines.",
                  "Review of statins and other non-pregnancy medicines.",
                  "Review of herbal or weight-loss products.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">
                Routine repetition should be guided by the baseline findings
                and clinical course.
              </p>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                10. Fetal Assessment Is Part of the Investigation Map
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  The fetal plan depends on whether diabetes existed around
                  conception or developed later.
                </p>
                <p>Pre-existing diabetes may require:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Accurate early dating.",
                  "Assessment of early pregnancy viability.",
                  "Detailed structural assessment.",
                  "Careful examination of the fetal heart.",
                  "Serial assessment of fetal growth and amniotic fluid.",
                  "Later fetal surveillance according to diabetes type, control and complications.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>
                  Gestational diabetes diagnosed later has a different
                  early-development context.
                </p>
                <p>
                  It may still affect fetal growth, amniotic fluid,
                  placental function and newborn glucose.
                </p>
                <p>
                  The complete fetal-surveillance plan will be discussed in
                  Part 4.
                </p>
              </div>
            </section>

            {/* Glucose Targets and Daily Monitoring */}
            <section id="glucose-targets-monitoring" className="scroll-mt-32 mt-16">
              <Overline>Targets & Monitoring</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                Glucose Targets and Daily Monitoring
              </h2>
              <div className="prose-rpl mt-5">
                <p>A target is useful only when:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "The timing of the measurement is clear.",
                  "The meter or sensor is functioning properly.",
                  "The woman can reach it without repeated hypoglycaemia.",
                  "The target is interpreted in the context of food, treatment and pregnancy stage.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Common Pregnancy Glucose Targets
              </h3>
              <div className="prose-rpl mt-4">
                <p>ADA and ACOG-aligned upper targets are:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  { text: "Fasting glucose: ", strong: "below 95 mg/dL", mid: " or ", strong2: "below 5.3 mmol/L" },
                  { text: "One hour after the meal: ", strong: "below 140 mg/dL", mid: " or ", strong2: "below 7.8 mmol/L" },
                  { text: "Two hours after the meal: ", strong: "below 120 mg/dL", mid: " or ", strong2: "below 6.7 mmol/L", cite: "1,3" },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>
                      {item.text}
                      <strong>{item.strong}</strong>
                      {item.mid}
                      <strong>{item.strong2}</strong>
                      {item.cite && <> <Cite>{item.cite}</Cite></>}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>
                  NICE uses the same fasting and one-hour targets, but a
                  two-hour target of:
                </p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                <li className="flex items-start gap-3">
                  <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                  <span>
                    <strong>below 115 mg/dL</strong> or <strong>below 6.4 mmol/L</strong> <Cite>4</Cite>
                  </span>
                </li>
              </ul>
              <div className="prose-rpl mt-6">
                <p>These are guideline targets—not moral pass marks.</p>
                <p>The treating team may individualise them when:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Significant hypoglycaemia occurs.",
                  "Hypoglycaemia awareness is impaired.",
                  "Severe vomiting limits intake.",
                  "Fetal growth is restricted.",
                  "Another maternal illness changes treatment safety.",
                  "The woman is transitioning rapidly from severe hyperglycaemia.",
                  "Meter or sensor accuracy is uncertain.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-6 bg-surfacealt/60 border-l-2 border-prestige p-6">
                <p className="text-[15px] text-navy leading-relaxed">
                  <strong>
                    The safest target is not simply the lowest possible
                    number. It is the best achievable glucose pattern
                    without dangerous hypoglycaemia or inadequate nutrition.
                  </strong>
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                When Does “After the Meal” Begin?
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  Post-meal timing should be agreed clearly and used
                  consistently.
                </p>
                <p>
                  It is commonly measured from the{" "}
                  <strong>start of the meal</strong>, rather than from the
                  final bite.
                </p>
                <p>
                  A one-hour result and a two-hour result answer different
                  questions.
                </p>
                <p>
                  They should not be compared as though they were taken at
                  the same metabolic point.
                </p>
                <p>The glucose log should state:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "The meal start time.",
                  "The testing time.",
                  "Whether the value is one-hour or two-hour.",
                  "Any delayed meal completion.",
                  "Vomiting.",
                  "Physical activity.",
                  "Insulin or medicine timing.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Who Usually Checks Which Values?
              </h3>

              <h4 className="font-serif text-xl text-navy tracking-tight leading-snug mt-8">
                Nutrition-Controlled Gestational Diabetes or Type 2 Diabetes
              </h4>
              <div className="prose-rpl mt-4">
                <p>A common starting pattern includes:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Fasting glucose.",
                  "One-hour or two-hour glucose after each main meal.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">
                Additional checks may be required when:
              </p>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Values are unstable.",
                  "Symptoms occur.",
                  "Food intake changes.",
                  "Medication is introduced.",
                  "Illness develops.",
                  "The pattern remains unexplained.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <h4 className="font-serif text-xl text-navy tracking-tight leading-snug mt-8">
                Insulin-Treated Type 1 or Type 2 Diabetes
              </h4>
              <div className="prose-rpl mt-4">
                <p>Monitoring may include:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Fasting glucose.",
                  "Pre-meal glucose.",
                  "One-hour or two-hour post-meal glucose.",
                  "Bedtime glucose.",
                  "Overnight glucose when indicated.",
                  "Additional checks for symptoms, exercise, driving, illness or correction doses.",
                  "Ketones when hyperglycaemic or unwell.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">
                The schedule should match the insulin regimen and the
                clinical question.
              </p>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Continuous Glucose Monitoring
              </h3>
              <div className="prose-rpl mt-4">
                <p>Continuous glucose monitoring can reveal:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Overnight patterns.",
                  "Post-meal peaks.",
                  "Direction and speed of glucose change.",
                  "Time spent in range.",
                  "Time below range.",
                  "Time above range.",
                  "Recurrent patterns that finger-prick testing may miss.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>ADA recommends CGM in pregnancy with type 1 diabetes.</p>
                <p>
                  It may also be helpful in selected women with type 2
                  diabetes or gestational diabetes, particularly when
                  glucose is unstable or hypoglycaemia is a concern.
                  <Cite>1,4</Cite>
                </p>
                <p>
                  CGM should not be interpreted from one attractive average
                  alone.
                </p>
                <p>The review should examine:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Sensor wear time.",
                  "Data completeness.",
                  "Overnight values.",
                  "Meal-related peaks.",
                  "Recurrent lows.",
                  "Alarm burden.",
                  "Treatment changes.",
                  "Whether symptoms match the sensor.",
                  "Whether a finger-prick confirmation is needed.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Pregnancy CGM Range for Type 1 Diabetes
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  The international pregnancy sensor range used by ADA is:
                </p>
                <p>
                  <strong>63–140 mg/dL</strong>, or{" "}
                  <strong>3.5–7.8 mmol/L</strong>
                </p>
                <p>
                  For pregnant women with type 1 diabetes, commonly used CGM
                  goals are:
                </p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  { text: "Time in range: ", strong: "more than 70%" },
                  { text: "Time above 140 mg/dL: ", strong: "less than 25%" },
                  { text: "Time below 63 mg/dL: ", strong: "less than 4%" },
                  { text: "Time below 54 mg/dL: ", strong: "less than 1%", cite: "1,5" },
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>
                      {item.text}
                      <strong>{item.strong}</strong>
                      {item.cite && <> <Cite>{item.cite}</Cite></>}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>
                  The same sensor range may be useful for reviewing type 2
                  diabetes and gestational diabetes.
                </p>
                <p>
                  Evidence is not yet sufficient to assign the same
                  percentage goals universally to every form of diabetes in
                  pregnancy.
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                When a Finger-Prick Check Still Matters
              </h3>
              <div className="prose-rpl mt-4">
                <p>A capillary glucose check may be required when:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Symptoms do not match the sensor.",
                  "The sensor shows a rapidly changing value.",
                  "Hypoglycaemia has just been treated.",
                  "The sensor is newly inserted.",
                  "Compression of the sensor is suspected.",
                  "A medication or insulin decision requires confirmation.",
                  "The sensor is not functioning.",
                  "Glucose is extremely high or low.",
                  "Ketones or acute illness are present.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>Technology supports clinical judgement.</p>
                <p>It does not remove the need to recognise an emergency.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Hypoglycaemia
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  Blood glucose below <strong>70 mg/dL</strong>, or{" "}
                  <strong>3.9 mmol/L</strong>, is generally treated as
                  hypoglycaemia.
                </p>
                <p>
                  For sensor glucose in pregnancy, a value below{" "}
                  <strong>63 mg/dL</strong>, or <strong>3.5 mmol/L</strong>,
                  is below the pregnancy sensor range.<Cite>1</Cite>
                </p>
                <p>Symptoms may include:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Shaking.",
                  "Sweating.",
                  "Hunger.",
                  "Palpitations.",
                  "Tingling.",
                  "Dizziness.",
                  "Weakness.",
                  "Blurred vision.",
                  "Difficulty concentrating.",
                  "Irritability.",
                  "Confusion.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">
                Severe hypoglycaemia may cause:
              </p>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Inability to self-treat.",
                  "Loss of consciousness.",
                  "Seizure.",
                  "Injury.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>
                  A woman using insulin should have an individual
                  hypoglycaemia plan.
                </p>
                <p>Family members may also require education.</p>
                <p>
                  Repeated low values are not proof of excellent diabetes
                  control.
                </p>
                <p>
                  They indicate that food, activity, insulin or medication
                  requires reassessment.
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Ketone Monitoring
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  Women with type 1 diabetes should have access to
                  blood-ketone testing and clear sick-day guidance.
                </p>
                <p>
                  Ketone testing may also be required in type 2 or
                  gestational diabetes when a woman is hyperglycaemic,
                  vomiting or unwell.
                </p>
                <p>
                  Urgent professional advice is required when ketones are
                  elevated or symptoms suggest ketoacidosis.
                </p>
                <p>
                  Pregnancy-related ketoacidosis can occur with glucose
                  below <strong>200 mg/dL</strong>, or{" "}
                  <strong>11.1 mmol/L</strong>.
                </p>
                <p>
                  A glucose result that does not appear dramatically high
                  must not provide false reassurance when the woman is ill.
                  <Cite>1,4,7</Cite>
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Reviewing the Log
              </h3>
              <div className="prose-rpl mt-4">
                <p>
                  The glucose record should be reviewed for patterns, not
                  isolated failures.
                </p>
                <p>Important questions include:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[
                  "Are fasting values repeatedly high?",
                  "Is one meal consistently producing the largest rise?",
                  "Are values high because the meal is unsuitable, the portion is excessive, insulin timing is mismatched or insulin is insufficient?",
                  "Are long fasting intervals producing ketones or rebound eating?",
                  "Is hypoglycaemia followed by overtreatment and a high reading?",
                  "Are weekends different from working days?",
                  "Is sleep affecting fasting glucose?",
                  "Is the sensor showing overnight instability?",
                  "Is the fetus growing too rapidly, appropriately or too slowly?",
                  "Has placental insulin resistance increased with gestational age?",
                  "Is the woman eating enough?",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">
                A treatment change should answer the pattern identified.
              </p>

              <div className="mt-6 bg-surfacealt/60 border-l-2 border-prestige p-6">
                <p className="text-[15px] text-navy leading-relaxed">
                  <strong>
                    The purpose of monitoring is not to collect more numbers.
                    It is to make the next decision more precise.
                  </strong>
                </p>
              </div>

              <p className="mt-8 text-[15px] text-stone2 leading-relaxed">
                The next section will explain how Dr Tania uses nutrition as
                active metabolic treatment while protecting pregnancy
                nutrition and recognising promptly when food alone is not
                sufficient.
              </p>
            </section>

            {/* --- PART 2 END --------------------------------------- */}

            {/* --- PART 3 START ------------------------------------- */}

            {/* Dr Tania's Nutrition-Led Approach */}
            <section id="nutrition-led-approach" className="scroll-mt-32 mt-16">
              <Overline>Nutrition</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                Dr Tania’s Nutrition-Led Approach
              </h2>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                Nutrition Is the Foundation—Not the Waiting Period Before Medicine
              </h3>
              <div className="prose-rpl mt-4">
                <p>In my practice, nutrition is active metabolic treatment.</p>
                <p>It does not mean handing every woman the same printed “diabetic diet.”</p>
                <p>It means examining how the individual woman’s food, meal timing, sleep, activity, weight, pregnancy symptoms, medicines and insulin interact with her glucose pattern.</p>
                <p>I have managed women who arrived with very high HbA1c values and substantial glucose elevation whose readings improved dramatically after an intensive, individualised dietary programme.</p>
                <p>In selected women with type 2 diabetes or gestational diabetes, close monitoring has allowed medication or insulin to be reduced substantially or withdrawn.</p>
                <p>That clinical possibility is real.</p>
                <p>It is not universal.</p>
                <p>It must never be extended automatically to type 1 diabetes, ketone-prone diabetes, suspected insulin deficiency or an acutely unwell woman.</p>
              </div>
              <div className="mt-6 bg-surfacealt/60 border-l-2 border-prestige p-6">
                <p className="text-[15px] text-navy leading-relaxed">
                  <strong>My approach is nutrition-led—not insulin-denying. Food is treated intensively, while insulin is continued whenever the woman’s physiology requires it.</strong>
                </p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">
                What the Nutrition Plan Is Designed to Achieve
              </h3>
              <div className="prose-rpl mt-4"><p>The plan is designed to:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Reduce fasting and post-meal glucose exposure.","Prevent large glucose excursions.","Avoid repeated hypoglycaemia.","Maintain adequate energy for pregnancy.","Provide sufficient protein and micronutrients.","Avoid prolonged fasting and starvation ketosis.","Support appropriate maternal weight gain.","Support healthy fetal growth.","Improve satiety and meal sustainability.","Reduce dependence on ultra-processed food and concentrated sugar.","Match carbohydrate intake with insulin when insulin is required.","Identify the smallest safe treatment burden that maintains control.","Remain practical within the woman’s culture, work and household."].map((i)=>(
                  <li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>The purpose is not simply to make the glucose meter look better for three days.</p>
                <p>The plan must remain nutritionally adequate and sustainable as pregnancy advances.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">There Is No Universal “Diabetes Plate”</h3>
              <div className="prose-rpl mt-4">
                <p>Two women with the same fasting glucose may need different food plans.</p>
                <p>The plan should consider:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Type 1, type 2 or gestational diabetes.","Current gestational age.","Pre-pregnancy weight.","Current weight gain.","Singleton or multiple pregnancy.","Nausea, vomiting or reflux.","Anaemia.","Kidney disease.","Liver disease.","Hypertension or pre-eclampsia.","Fetal growth.","Food preferences.","Vegetarian or non-vegetarian diet.","Food availability.","Work shifts.","Religious fasting.","Insulin regimen.","Hypoglycaemia.","Ketone tendency.","The glucose response to individual meals."].map((i)=>(
                  <li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>
                ))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">A woman with severe obesity, post-meal hyperglycaemia and excessive fetal growth cannot be given the same plan as an underweight woman carrying twins who is vomiting and producing ketones.</p>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Carbohydrate Is Managed—not Automatically Eliminated</h3>
              <div className="prose-rpl mt-4">
                <p>Carbohydrate affects post-meal glucose more directly than protein or fat.</p>
                <p>This does not mean that every carbohydrate-containing food should be removed.</p>
                <p>Pregnancy requires adequate energy and nutrient intake.</p>
                <p>International dietary guidance commonly uses a pregnancy reference intake of at least <strong>175 g of carbohydrate per day</strong>, together with at least <strong>71 g of protein</strong> and <strong>28 g of fibre</strong>.</p>
                <p>These figures are population-level reference values.</p>
                <p>They are not a substitute for individual assessment.</p>
                <p>In practice, carbohydrate quantity, quality and distribution should be reviewed together with:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[{t:"Glucose results."},{t:"Ketones."},{t:"Maternal intake."},{t:"Weight trajectory."},{t:"Insulin requirement."},{t:"Fetal growth."},{t:"Tolerance of food."},{t:"Overall nutrient adequacy.",cite:"1"}].map((it,i)=>(
                  <li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{it.t}{it.cite && <> <Cite>{it.cite}</Cite></>}</span></li>
                ))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>I do not recommend an unsupervised ketogenic diet in pregnancy.</p>
                <p>I also do not assume that adding more fat is metabolically harmless merely because carbohydrate has been reduced.</p>
                <p>Severe carbohydrate restriction may increase ketone production, reduce dietary variety and unintentionally increase saturated-fat intake.</p>
              </div>
              <div className="mt-6 bg-surfacealt/60 border-l-2 border-prestige p-6">
                <p className="text-[15px] text-navy leading-relaxed"><strong>The objective is not “zero carbohydrate.” The objective is the right carbohydrate, in the right quantity, at the right time, for the individual pregnancy.</strong></p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Carbohydrate Quality Matters</h3>
              <div className="prose-rpl mt-4"><p>The plan usually gives preference to suitable portions of:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Whole grains.","Pulses and legumes.","Beans.","Vegetables.","Whole fruit.","Nuts and seeds.","Minimally processed traditional foods."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">It usually limits:</p>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Sweetened beverages.","Fruit juice.","Refined sweets.","Large quantities of added sugar.","Jaggery, honey and syrups used as though they are glucose-free.","Highly refined flour products.","Ultra-processed snacks.","Large portions of rapidly absorbed starch.","Foods repeatedly shown to produce excessive glucose rises."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>“Natural” sugar still affects glucose.</p>
                <p>“Diabetic” packaged food is not automatically nutritious.</p>
                <p>A whole food can still require portion adjustment when the glucose response is excessive.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Indian Meals Must Be Assessed as Complete Meals</h3>
              <div className="prose-rpl mt-4">
                <p>Rice, roti, millet, dal, fruit and milk are not universally prohibited.</p>
                <p>The metabolic response depends on:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Portion.","Preparation.","Accompanying protein.","Fibre.","Fat content.","Meal timing.","Whether several starches are combined.","Activity after eating.","The woman’s insulin resistance.","The insulin dose and timing when insulin is used."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>A meal containing rice, roti, potato and a sweet may produce a very different glucose response from a meal containing one measured starch, vegetables and adequate protein.</p>
                <p>The solution is not to label one cultural staple as the enemy.</p>
                <p>It is to identify the total glucose load and reconstruct the plate.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Protein Is Planned Deliberately</h3>
              <div className="prose-rpl mt-4"><p>Protein supports:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Maternal tissues.","Placental growth.","Fetal growth.","Satiety.","Preservation of lean mass.","A more balanced meal response."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">Suitable sources may include:</p>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Pulses and legumes.","Beans.","Lentils.","Soy foods.","Curd or yoghurt.","Paneer in an appropriate preparation and portion.","Milk when tolerated and metabolically suitable.","Eggs.","Fish.","Poultry.","Lean meat.","Nuts and seeds."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>Vegetarian diets can provide adequate protein.</p>
                <p>They require deliberate planning rather than assuming that cereal plus a small quantity of dal is automatically sufficient.</p>
                <p>Kidney disease, severe vomiting, food intolerance or another medical condition may require specialist adjustment.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Fruit Is Individualised—not Banned Automatically</h3>
              <div className="prose-rpl mt-4"><p>Whole fruit provides:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Fibre.","Vitamins.","Minerals.","Phytochemicals.","Hydration."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>Fruit juice produces a different glucose exposure because much of the intact structure and fibre has been removed.</p>
                <p>The fruit plan should consider:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Type of fruit.","Portion.","Ripeness.","Timing.","Whether it is eaten alone or with another food.","The woman’s measured glucose response.","Total daily intake."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>Removing every fruit without assessing the actual pattern may reduce dietary quality unnecessarily.</p>
                <p>Repeated excessive glucose rises after a particular fruit or portion justify adjustment.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Fat Quality Matters</h3>
              <div className="prose-rpl mt-4">
                <p>Replacing carbohydrate with unlimited fat is not a safe strategy.</p>
                <p>The plan usually favours:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Nuts.","Seeds.","Suitable plant oils.","Fish providing omega-3 fatty acids.","Other unsaturated-fat sources."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">It limits:</p>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Trans fats.","Repeatedly heated oil.","Excessive saturated fat.","Deep-fried food.","Large quantities of fatty processed meat.","Ultra-processed foods in which fat, refined starch and salt are combined."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>Fat can delay gastric emptying and produce a later glucose rise.</p>
                <p>A satisfactory one-hour value does not always exclude a delayed post-meal rise after a very high-fat meal.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Fibre Is Increased Thoughtfully</h3>
              <div className="prose-rpl mt-4"><p>Fibre can support:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Satiety.","Bowel regularity.","Slower carbohydrate absorption.","Overall dietary quality."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">Pregnancy guidance commonly recommends approximately <strong>28 g of fibre per day</strong>. <Cite>1</Cite></p>
              <p className="mt-4 text-[15px] text-stone2 leading-relaxed">The increase should be gradual when the woman has:</p>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Bloating.","Constipation.","Irritable bowel symptoms.","Nausea.","Reduced fluid intake.","Significant reflux."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">Fibre supplements are not automatically equivalent to a balanced whole-food plan.</p>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Meal Timing Is Part of Treatment</h3>
              <div className="prose-rpl mt-4">
                <p>The metabolic day is influenced not only by what is eaten, but by when it is eaten.</p>
                <p>The plan may address:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Delayed first meals.","Long fasting intervals.","Large evening meals.","Repeated grazing.","Work-shift eating.","Bedtime snacks.","Overnight hypoglycaemia.","Morning insulin resistance.","Vomiting and inability to maintain intake."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>A woman with high fasting glucose does not automatically need a bedtime snack.</p>
                <p>Another woman may require one to prevent overnight hypoglycaemia or excessive fasting.</p>
                <p>The decision should be based on her overnight and fasting pattern.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Breakfast Often Requires Special Attention</h3>
              <div className="prose-rpl mt-4">
                <p>Pregnancy-related insulin resistance and the dawn hormonal pattern may make breakfast glucose difficult to control.</p>
                <p>Breakfast may therefore require adjustment of:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Carbohydrate amount.","Carbohydrate type.","Protein content.","Meal timing.","Insulin timing when used.","Physical activity after eating."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>This does not justify skipping breakfast automatically.</p>
                <p>Prolonged fasting may worsen nausea, encourage later overeating or contribute to ketone production in susceptible women.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Long Fasting Is Not a Diabetes Treatment</h3>
              <div className="prose-rpl mt-4">
                <p>I do not recommend prolonged fasting as a routine method of controlling pregnancy glucose.</p>
                <p>Long fasting intervals may lead to:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Hunger and rebound eating.","Nutrient inadequacy.","Ketone production.","Headache or weakness.","Hypoglycaemia in medicated women.","Difficulty matching insulin.","An unsustainable daily pattern."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>Religious fasting requires an individual medical discussion.</p>
                <p>Women with type 1 diabetes, unstable glucose, recurrent hypoglycaemia, ketones, vomiting or pregnancy complications may face substantial risk.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Weight Management Does Not Mean Weight-Loss Dieting</h3>
              <div className="prose-rpl mt-4"><p>The food plan should support appropriate pregnancy weight gain according to:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Pre-pregnancy body mass index.","Gestational age.","Singleton or multiple pregnancy.","Fetal growth.","Maternal health."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>Intentional weight-loss dieting is not routinely recommended during pregnancy.</p>
                <p>For a woman with overweight or obesity, the objective is commonly to prevent excessive gain while maintaining nutritional adequacy—not to impose starvation or rapid loss.</p>
                <p>For an underweight woman or one with inadequate gain, glucose control must not be achieved by withholding necessary food.</p>
              </div>
              <div className="mt-6 bg-surfacealt/60 border-l-2 border-prestige p-6"><p className="text-[15px] text-navy leading-relaxed"><strong>A smaller glucose rise obtained by underfeeding the mother is not successful diabetes care.</strong></p></div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Physical Activity Is Prescribed According to Obstetric Safety</h3>
              <div className="prose-rpl mt-4">
                <p>For women without an obstetric or medical contraindication, regular physical activity can improve insulin sensitivity and post-meal glucose.</p>
                <p>General pregnancy guidance commonly recommends at least <strong>150 minutes of moderate-intensity aerobic activity per week</strong>, spread across the week. <Cite>1</Cite></p>
                <p>Short periods of movement after meals may be particularly useful for some women.</p>
                <p>The plan may include:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Walking.","Pregnancy-appropriate strength work.","Mobility exercises.","Breaking up prolonged sitting.","Brief post-meal activity."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">Activity must be individualised when there is:</p>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Bleeding.","Placenta previa at a relevant gestational stage.","Cervical insufficiency.","Threatened or established preterm labour.","Ruptured membranes.","Severe anaemia.","Significant heart or lung disease.","Severe hypertension or pre-eclampsia.","Fetal growth restriction requiring activity modification.","Another obstetric restriction."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">Exercise is not used to compensate for severe hyperglycaemia, ketones or acute illness.</p>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">The Glucose Response Teaches Us Whether the Plan Is Working</h3>
              <div className="prose-rpl mt-4">
                <p>A food plan should produce measurable evidence.</p>
                <p>I review:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Fasting glucose.","Post-meal glucose.","Overnight values when available.","Hypoglycaemia.","Ketones when indicated.","Hunger and satiety.","Maternal weight.","Nutritional adequacy.","Fetal growth.","The woman’s ability to sustain the plan."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>A rapid improvement may justify careful reduction of glucose-lowering treatment.</p>
                <p>Failure to reach targets despite genuine implementation means the plan or treatment must change.</p>
                <p>The woman should not be blamed for placental insulin resistance that requires medication.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">When Nutrition Alone May Be Sufficient</h3>
              <div className="prose-rpl mt-4"><p>Nutrition and physical activity may be sufficient when:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["The diabetes type does not require insulin for survival.","Fasting and post-meal targets are reached consistently.","Ketones are absent when assessed.","Maternal intake remains adequate.","Hypoglycaemia is not occurring.","Fetal growth is appropriate.","The pattern remains controlled as gestation advances.","The woman can monitor reliably.","There is a clear escalation plan if control worsens."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>Many women with gestational diabetes can remain controlled through lifestyle treatment alone. <Cite>1</Cite></p>
                <p>Selected women with type 2 diabetes may also demonstrate substantial improvement.</p>
                <p>The decision must be confirmed from monitored response.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">When Nutrition Alone Is Not Enough</h3>
              <div className="prose-rpl mt-4"><p>Medication or insulin should not be delayed when:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Fasting glucose remains repeatedly above target.","Post-meal glucose remains repeatedly above target.","Hyperglycaemia is severe.","Ketones or insulin deficiency are present.","The woman is acutely unwell.","Adequate nutrition cannot be reduced further safely.","Fetal overgrowth or polyhydramnios suggests ongoing glucose exposure.","Gestational insulin resistance is increasing.","Type 1 diabetes is present.","The woman cannot maintain the required monitoring or plan.","Another maternal–fetal concern requires faster control."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="mt-6 bg-surfacealt/60 border-l-2 border-prestige p-6"><p className="text-[15px] text-navy leading-relaxed"><strong>Diet is not proved by refusing medicine. Diet is proved by the glucose pattern it achieves while mother and baby remain adequately nourished and safe.</strong></p></div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">What I Do Not Recommend</h3>
              <div className="prose-rpl mt-4"><p>I do not recommend:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Starvation.","Prolonged fasting as routine therapy.","Ketogenic dieting during pregnancy.","Zero-carbohydrate plans.","Juice-only plans.","“Diabetic” packaged foods accepted without reading their composition.","Replacing carbohydrate with unlimited saturated fat.","Removing all fruit automatically.","Removing all milk automatically.","Herbal glucose-lowering products.","Unregulated supplements.","Abrupt medication changes without monitoring.","Abrupt insulin withdrawal.","Ignoring ketones because the glucose is not extremely high.","Judging treatment success from HbA1c alone.","Continuing an ineffective food plan merely to avoid medication."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Why a General Website Cannot Prescribe One Meal Plan</h3>
              <div className="prose-rpl mt-4"><p>The same menu is not appropriate for:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Type 1 diabetes using an insulin pump.","Gestational diabetes controlled without medication.","Type 2 diabetes with an HbA1c of 13%.","Recurrent hypoglycaemia.","Twin pregnancy.","Severe vomiting.","Kidney disease.","Anaemia and inadequate weight gain.","Severe obesity with excessive fetal growth.","Vegetarian pregnancy with low protein intake.","Night-shift work.","Food insecurity.","A woman producing ketones after carbohydrate restriction."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>The detailed plan belongs in an individual consultation.</p>
                <p>The website can explain principles.</p>
                <p>It cannot safely calculate the woman’s food, insulin and metabolic needs without her complete clinical picture.</p>
              </div>
            </section>

            {/* Medicines, Insulin and Safety */}
            <section id="medicines-insulin-safety" className="scroll-mt-32 mt-16">
              <Overline>Medicines & Insulin</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">Medicines, Insulin and Safety</h2>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Treatment Is Chosen by Physiology and Response</h3>
              <div className="prose-rpl mt-4"><p>Diabetes treatment in pregnancy may involve:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Nutrition and physical activity alone.","Insulin.","Metformin in selected circumstances.","Diabetes technology.","Treatment of associated hypertension, kidney disease or another condition.","Rapid hospital-based treatment during metabolic emergencies."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>The objective is not to use the fewest medicines at any cost.</p>
                <p>The objective is to use the least burdensome treatment that reaches pregnancy targets safely and reliably.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Type 1 Diabetes Requires Insulin</h3>
              <div className="prose-rpl mt-4">
                <p>Insulin is essential in type 1 diabetes.</p>
                <p>It cannot be replaced by:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Diet.","Exercise.","Metformin.","Herbal treatment.","Supplements.","Weight loss.","An insulin pump without insulin inside it."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>The dose may fall in early pregnancy.</p>
                <p>It may change with vomiting, activity or food intake.</p>
                <p>It usually rises as placental insulin resistance increases later in pregnancy.</p>
                <p>The need for adjustment does not mean that insulin can be stopped.</p>
              </div>
              <div className="mt-6 bg-surfacealt/60 border-l-2 border-prestige p-6"><p className="text-[15px] text-navy leading-relaxed"><strong>In type 1 diabetes, nutrition changes the insulin requirement. It does not remove the requirement for insulin.</strong></p></div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Insulin in Type 2 Diabetes and Gestational Diabetes</h3>
              <div className="prose-rpl mt-4">
                <p>ADA recommends insulin as the preferred glucose-lowering medicine for type 2 diabetes and gestational diabetes in pregnancy when medication is required. <Cite>1</Cite></p>
                <p>Insulin:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Can be adjusted to the glucose pattern.","Does not cross the placenta in clinically meaningful amounts.","Can target fasting, meal-related or combined hyperglycaemia.","Has extensive pregnancy experience."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">Its disadvantages include:</p>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Injections or pump use.","Hypoglycaemia.","Weight gain.","Cost and access.","The need for education and dose adjustment.","Anxiety or stigma for some women."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>A woman should not be told that needing insulin means she has failed.</p>
                <p>Placental insulin resistance may exceed what nutrition and her own insulin production can overcome.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">When Insulin May Be Reduced</h3>
              <div className="prose-rpl mt-4"><p>Insulin may require reduction when:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Nutrition has changed substantially.","Glucose values fall below target.","Hypoglycaemia occurs.","Vomiting reduces intake.","Physical activity increases.","Steroid-related hyperglycaemia resolves.","An acute infection improves.","The original dose was excessive.","Early-pregnancy insulin sensitivity increases.","A meal-related glucose load has been corrected.","The presumed diagnosis is being reconsidered under specialist supervision."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">Reduction should be based on:</p>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Repeated glucose data.","The type and timing of insulin.","Food intake.","Ketones.","Symptoms.","Gestational age.","The reason insulin was started."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>A reduction is a treatment adjustment.</p>
                <p>It is not proof that the original clinician was wrong or that insulin is harmful.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">When Insulin May Be Withdrawn in Selected Women</h3>
              <div className="prose-rpl mt-4">
                <p>Selected women with gestational diabetes or type 2 diabetes may become controlled without insulin after intensive dietary correction and close monitoring.</p>
                <p>Before withdrawal, the clinician should establish that:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Type 1 diabetes is not present.","Insulin deficiency is not suspected.","Ketones are absent when relevant.","Current fasting and post-meal values are consistently within target.","Maternal intake is adequate.","Hypoglycaemia is not driving an artificial low average.","The woman can monitor frequently during the transition.","There is a clear threshold for restarting treatment.","Follow-up is sufficiently close.","Fetal growth and the wider pregnancy remain appropriate."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">Insulin should usually be reduced stepwise rather than stopped blindly when the existing dose is substantial or the metabolic diagnosis remains uncertain.</p>
              <div className="mt-6 bg-surfacealt/60 border-l-2 border-prestige p-6"><p className="text-[15px] text-navy leading-relaxed"><strong>Stopping insulin is not the goal. Safe control without unnecessary insulin may be the result in a carefully selected woman.</strong></p></div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">When Insulin Must Not Be Stopped</h3>
              <div className="prose-rpl mt-4"><p>Insulin must not be stopped in:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Confirmed type 1 diabetes.","Probable type 1 diabetes awaiting clarification.","Active diabetic ketoacidosis.","Significant ketonaemia caused by insulin deficiency.","Pump-dependent type 1 diabetes without an immediate alternative insulin plan.","Severe hyperglycaemia that remains uncontrolled.","A woman who becomes metabolically unwell after dose reduction."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">Urgent review is also required when insulin reduction is followed by:</p>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Rising fasting glucose.","Rising post-meal glucose.","Ketones.","Weight loss.","Vomiting.","Excessive thirst.","Frequent urination.","Weakness.","Abdominal pain.","Rapid breathing."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Insulin Requirements Change During Pregnancy</h3>
              <div className="prose-rpl mt-4">
                <p>In early pregnancy, insulin requirements may fall, particularly in type 1 diabetes.</p>
                <p>The risk of hypoglycaemia may increase.</p>
                <p>From approximately 16 weeks, placental insulin resistance commonly rises.</p>
                <p>Insulin requirements may increase progressively and can become substantially higher than pre-pregnancy requirements by late pregnancy. <Cite>1</Cite></p>
                <p>This change is physiological.</p>
                <p>It does not mean that the woman’s diet has suddenly failed.</p>
                <p>It means that the placenta has changed the metabolic environment.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">A Sudden Fall in Late-Pregnancy Insulin Requirement</h3>
              <div className="prose-rpl mt-4"><p>A late-pregnancy fall in insulin requirement may occur because of:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Reduced food intake.","Vomiting.","Increased activity.","Improved meal composition.","A dosing error.","Reduced insulin clearance.","Placental change."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>A rapid and substantial unexplained fall may prompt assessment of maternal and placental wellbeing.</p>
                <p>It does not diagnose placental insufficiency by itself because the evidence is not consistent.</p>
                <p>The finding should be interpreted with:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[{t:"Fetal movements."},{t:"Blood pressure."},{t:"Fetal growth."},{t:"Amniotic fluid."},{t:"Surveillance findings."},{t:"Maternal symptoms."},{t:"The complete glucose record.",cite:"1"}].map((it,i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{it.t}{it.cite && <> <Cite>{it.cite}</Cite></>}</span></li>))}
              </ul>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Basal and Mealtime Insulin Answer Different Problems</h3>
              <div className="prose-rpl mt-4">
                <p>Basal insulin is used principally to address glucose between meals and overnight.</p>
                <p>Mealtime insulin is used principally to address the rise associated with food.</p>
                <p>Mixed insulin combines components in fixed proportions.</p>
                <p>An insulin pump delivers rapid-acting insulin continuously and allows programmed basal rates and meal dosing.</p>
                <p>The choice depends on:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Diabetes type.","Glucose pattern.","Previous regimen.","Ability to use the treatment safely.","Cost and access.","Hypoglycaemia.","Lifestyle.","Pregnancy stage.","Specialist experience."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>A fasting problem should not automatically be treated by removing food from every meal.</p>
                <p>A meal-related rise should not automatically be treated by escalating overnight insulin.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Insulin Timing and Technique Matter</h3>
              <div className="prose-rpl mt-4"><p>Before declaring insulin ineffective, review:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Correct insulin identity.","Correct dose.","Injection timing.","Injection site.","Site rotation.","Lipohypertrophy.","Needle technique.","Storage.","Expiry.","Pen priming.","Pump cannula function.","Missed doses.","Delayed meals.","Correction stacking.","Carbohydrate estimation.","Hypoglycaemia overtreatment."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">A technical error can mimic severe treatment resistance.</p>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Insulin Pumps and Automated Insulin Delivery</h3>
              <div className="prose-rpl mt-4">
                <p>Multiple daily injections and insulin-pump therapy can both be used in pregnancy complicated by type 1 diabetes.</p>
                <p>ADA 2026 recommends automated insulin-delivery systems with pregnancy-specific glucose targets for pregnant women with type 1 diabetes.</p>
                <p>Systems without pregnancy-specific targets may be considered for selected women using additional strategies and an experienced diabetes-pregnancy team. <Cite>1</Cite></p>
                <p>Technology does not eliminate the need for:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Meal planning.","Bolus timing.","Infusion-site checks.","Ketone testing.","Backup insulin.","Emergency pump-failure instructions.","Review of sensor accuracy."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>Because pump therapy uses rapid-acting insulin, interruption of delivery can lead to ketosis more quickly than many patients expect.</p>
                <p>Unexplained high glucose on a pump requires prompt troubleshooting and ketone assessment.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Metformin</h3>
              <div className="prose-rpl mt-4">
                <p>Metformin reduces hepatic glucose production and improves insulin sensitivity.</p>
                <p>Guidelines differ in how it is positioned during pregnancy.</p>
                <p>ADA does not recommend metformin as a first-line glucose-lowering medicine for diabetes in pregnancy because it crosses the placenta and may not achieve targets in all women.</p>
                <p>NICE permits metformin in selected women with gestational diabetes or pre-existing type 2 diabetes when diet and activity do not reach targets, when insulin is unacceptable or as an addition to insulin. <Cite>1,4</Cite></p>
                <p>The discussion should include:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["The reason it is being considered.","The likelihood that insulin may still be required.","Gastrointestinal effects.","Kidney function.","Placental transfer.","The limits of long-term offspring evidence.","The woman’s informed preference.","Local guideline practice."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">Metformin is not a substitute for insulin in type 1 diabetes.</p>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Metformin Used for Polycystic Ovary Syndrome</h3>
              <div className="prose-rpl mt-4">
                <p>When metformin was used to treat polycystic ovary syndrome and assist ovulation, ADA recommends discontinuing it by the end of the first trimester unless another clear diabetes-related indication exists. <Cite>1</Cite></p>
                <p>The medicine should not be continued automatically throughout pregnancy merely because conception occurred while taking it.</p>
                <p>The indication should be reassessed.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Glyburide or Glibenclamide</h3>
              <div className="prose-rpl mt-4">
                <p>Glyburide, also called glibenclamide, crosses the placenta.</p>
                <p>ADA does not recommend it as first-line treatment for diabetes in pregnancy.</p>
                <p>Concerns include:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Failure to achieve glucose targets in some women.","Maternal hypoglycaemia.","Neonatal hypoglycaemia.","Less reassuring outcome data than insulin."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>Its position varies between healthcare systems.</p>
                <p>It should not be started or continued without an informed specialist review. <Cite>1,3</Cite></p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Other Diabetes Medicines</h3>
              <div className="prose-rpl mt-4">
                <p>Other oral and injectable glucose-lowering medicines generally lack sufficient pregnancy safety data for routine use.</p>
                <p>These include:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["SGLT2 inhibitors.","GLP-1 receptor agonists.","Dual GIP/GLP-1 receptor agonists.","DPP-4 inhibitors.","Thiazolidinediones.","Most other non-insulin glucose-lowering agents."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>They are not routine pregnancy treatments.</p>
                <p>Women planning pregnancy should have these medicines reviewed before conception whenever possible.</p>
                <p>A woman who discovers pregnancy while taking one should seek prompt medical review rather than stopping every treatment without a replacement plan.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">SGLT2 Inhibitors and Ketoacidosis</h3>
              <div className="prose-rpl mt-4">
                <p>SGLT2 inhibitors are not recommended during pregnancy.</p>
                <p>They can be associated with diabetic ketoacidosis even when glucose is not extremely high.</p>
                <p>A pregnant woman who has recently used an SGLT2 inhibitor and develops vomiting, abdominal pain, weakness, breathlessness or ketones requires urgent medical assessment.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Herbal and “Natural” Glucose-Lowering Products</h3>
              <div className="prose-rpl mt-4"><p>Herbal products may:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Contain uncertain ingredients.","Vary between batches.","Interact with medicines.","Cause liver or kidney injury.","Produce hypoglycaemia.","Lack fetal-safety data.","Delay effective treatment."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>“Natural” does not mean pregnancy-safe.</p>
                <p>They should not be used as substitutes for monitored nutrition, insulin or evidence-based treatment.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Aspirin and Associated Risk Reduction</h3>
              <div className="prose-rpl mt-4">
                <p>Women with pre-existing type 1 or type 2 diabetes have an increased risk of pre-eclampsia.</p>
                <p>Low-dose aspirin is commonly recommended from the end of the first trimester for eligible women, according to the treating clinician and local guideline. <Cite>1,3,4</Cite></p>
                <p>The exact preparation, dose and timing should be prescribed individually.</p>
                <p>Aspirin is not a treatment for glucose.</p>
                <p>It addresses an associated pregnancy risk.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Medicines for Blood Pressure, Cholesterol and Kidney Protection</h3>
              <div className="prose-rpl mt-4"><p>Some medicines commonly used outside pregnancy require review before or as soon as pregnancy is recognised.</p><p>These include:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["ACE inhibitors.","Angiotensin-receptor blockers.","Statins.","Some blood-pressure medicines.","Some kidney-protective medicines."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">The correct action depends on:</p>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["The medicine.","The indication.","Gestational timing.","Maternal condition.","Available pregnancy-compatible alternatives."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">A woman should not continue or stop essential cardiovascular treatment without prompt medical advice.</p>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Steroids and Glucose</h3>
              <div className="prose-rpl mt-4">
                <p>Antenatal corticosteroids may be recommended to improve fetal lung maturity when preterm birth is anticipated.</p>
                <p>They can raise glucose substantially.</p>
                <p>A woman with diabetes may require:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["More frequent glucose monitoring.","Temporary insulin introduction.","Temporary insulin-dose increase.","Ketone monitoring.","Hospital supervision in selected circumstances."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>The glucose effect is expected.</p>
                <p>It should be planned for rather than used as a reason to withhold indicated fetal treatment.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Illness, Vomiting and Sick-Day Safety</h3>
              <div className="prose-rpl mt-4"><p>During illness:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Insulin requirements may rise.","Food intake may fall.","Ketones may develop.","Dehydration may worsen glucose.","Type 1 diabetes insulin must not be stopped.","Pump failure must be excluded when glucose rises unexpectedly."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">The woman should have an individual sick-day plan covering:</p>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Glucose-testing frequency.","Ketone testing.","Fluids.","Insulin.","Hypoglycaemia prevention.","When to contact the care team.","When to seek emergency assessment."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">A website cannot safely replace that personalised plan.</p>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">The Treatment Plan Must Remain Dynamic</h3>
              <div className="prose-rpl mt-4"><p>Treatment may need revision because of:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Advancing gestation.","Placental insulin resistance.","Nutrition response.","Vomiting.","Infection.","Steroid administration.","Reduced or increased activity.","Fetal growth.","Hypoglycaemia.","Kidney or liver function.","Labour planning.","Delivery.","Breastfeeding."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">A woman should not be left on the same treatment merely because it was correct at the previous visit.</p>
              <div className="mt-6 bg-surfacealt/60 border-l-2 border-prestige p-6"><p className="text-[15px] text-navy leading-relaxed"><strong>The best diabetes plan in pregnancy is not the one that proves a philosophy. It is the one that keeps changing accurately as the mother, placenta and baby change.</strong></p></div>

              <p className="mt-8 text-[15px] text-stone2 leading-relaxed">
                The next part will explain what diabetes can mean for the mother and baby, how fetal growth and surveillance are planned, and how the timing and mode of birth are individualised.
              </p>
            </section>

            {/* --- PART 3 END --------------------------------------- */}

            {/* --- PART 4 START ------------------------------------- */}

            {/* What Diabetes Can Mean for the Mother */}
            <section id="maternal-risks" className="scroll-mt-32 mt-16">
              <Overline>Maternal Implications</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">What Diabetes Can Mean for the Mother</h2>
              <div className="prose-rpl mt-5">
                <p>Most women with diabetes can have a successful pregnancy.</p>
                <p>The risks are not identical for every woman.</p>
                <p>They depend on:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["The type and duration of diabetes.","Glucose control before conception.","Glucose control during pregnancy.","Hypoglycaemia and ketone risk.","Blood pressure.","Kidney, eye and vascular health.","Maternal weight.","Other pregnancy complications.","Fetal growth.","Access to coordinated care."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>Good glucose control reduces many risks.</p>
                <p>It does not reduce every risk to zero. <Cite>1,4</Cite></p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Hypoglycaemia</h3>
              <div className="prose-rpl mt-4">
                <p>Hypoglycaemia is particularly important in women using insulin.</p>
                <p>The risk may increase:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["In early pregnancy.","When nausea or vomiting reduces food intake.","After a major dietary change.","After physical activity.","Overnight.","When insulin doses are not reduced as sensitivity improves.","During breastfeeding after birth."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">Pregnancy may reduce awareness of the usual warning symptoms.</p>
              <p className="mt-3 text-[15px] text-stone2 leading-relaxed">Repeated low glucose can cause:</p>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Confusion.","Falls.","Injury.","Loss of consciousness.","Seizure.","Fear of taking necessary insulin.","Rebound eating and glucose instability."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">The objective is not to reach pregnancy targets by accepting recurrent hypoglycaemia.</p>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Diabetic Ketoacidosis</h3>
              <div className="prose-rpl mt-4">
                <p>Diabetic ketoacidosis is a medical emergency.</p>
                <p>Pregnancy increases susceptibility to ketosis, and ketoacidosis may occur at lower glucose levels than outside pregnancy.</p>
                <p>Risk is higher with:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Type 1 diabetes.","Missed or interrupted insulin.","Insulin-pump failure.","Vomiting.","Infection.","Steroid administration.","Severe dehydration.","Prolonged fasting.","Inadequate carbohydrate intake in a susceptible woman.","Recent SGLT2-inhibitor exposure.","Previously unrecognised insulin deficiency."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>Ketoacidosis threatens both maternal and fetal life.</p>
                <p>It requires immediate hospital treatment—not outpatient dietary adjustment. <Cite>1,4,7</Cite></p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Preeclampsia and Hypertensive Disease</h3>
              <div className="prose-rpl mt-4">
                <p>Type 1 and type 2 diabetes increase the risk of pre-eclampsia.</p>
                <p>The risk may be further increased by:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Long duration of diabetes.","Kidney disease.","Chronic hypertension.","Obesity.","Previous pre-eclampsia.","Multiple pregnancy.","Poor glucose control.","Vascular disease."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">Assessment includes:</p>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Blood pressure.","Urine protein.","Symptoms.","Platelets.","Kidney and liver function when indicated.","Fetal growth and placental assessment."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>Low-dose aspirin is commonly prescribed for eligible women with pre-existing diabetes.</p>
                <p>It should be prescribed according to the complete risk profile and local guideline—not self-started. <Cite>1,3,4</Cite></p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Diabetic Retinopathy</h3>
              <div className="prose-rpl mt-4">
                <p>Pregnancy can accelerate diabetic-retinopathy progression.</p>
                <p>Risk is influenced by:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Retinopathy present before pregnancy.","Duration of diabetes.","Baseline glucose control.","Rapid improvement from severe hyperglycaemia.","Hypertension.","Kidney disease."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>Rapid glucose improvement should not be withheld merely because retinopathy is present.</p>
                <p>It should be coordinated with retinal assessment and treatment when required. <Cite>1,4</Cite></p>
                <p>Visual symptoms such as sudden blurring, flashes, floaters or loss of vision require urgent assessment.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Diabetic Kidney Disease</h3>
              <div className="prose-rpl mt-4"><p>Kidney disease may increase the risk of:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Hypertension.","Pre-eclampsia.","Worsening proteinuria.","Decline in maternal kidney function.","Fetal growth restriction.","Preterm birth.","Earlier medically indicated delivery."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>Proteinuria may rise during pregnancy even without pre-eclampsia.</p>
                <p>New or worsening hypertension, symptoms, laboratory changes and the fetal picture help distinguish chronic kidney disease from superimposed pre-eclampsia.</p>
                <p>Specialist nephrology and maternal–fetal-medicine care may be required.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Cardiovascular and Vascular Disease</h3>
              <div className="prose-rpl mt-4"><p>Long-standing diabetes may coexist with:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Coronary disease.","Autonomic neuropathy.","Peripheral vascular disease.","Previous stroke.","Significant hypertension.","Dyslipidaemia.","Obesity or sleep apnoea."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>Pregnancy increases cardiovascular workload.</p>
                <p>Women with symptoms, established disease or major risk factors may require cardiology and anaesthetic assessment.</p>
                <p>Chest pain, severe breathlessness, fainting or sudden neurological symptoms require urgent evaluation.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Infections</h3>
              <div className="prose-rpl mt-4"><p>Diabetes may increase susceptibility to:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Urinary infection.","Recurrent candidiasis.","Skin infection.","Wound infection after birth.","More severe metabolic disturbance during illness."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>Infection can also raise glucose and precipitate ketoacidosis.</p>
                <p>Persistent high glucose during illness should not be attributed to food alone.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Polyhydramnios</h3>
              <div className="prose-rpl mt-4">
                <p>Excess amniotic fluid may occur when fetal glucose exposure leads to increased fetal urine production.</p>
                <p>Polyhydramnios may increase the likelihood of:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Maternal discomfort.","Breathlessness.","Preterm contractions.","Malpresentation.","Cord prolapse after membrane rupture.","Postpartum haemorrhage.","Additional fetal surveillance."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>Polyhydramnios also has causes unrelated to diabetes.</p>
                <p>The fetus and pregnancy should be assessed rather than assuming glucose is the only explanation.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Preterm Birth</h3>
              <div className="prose-rpl mt-4"><p>Preterm birth may occur because:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Labour begins spontaneously.","Polyhydramnios causes uterine overdistension.","Pre-eclampsia develops.","Fetal growth becomes abnormal.","Maternal kidney or vascular disease worsens.","Fetal surveillance becomes concerning.","Metabolic control becomes unsafe."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>Antenatal corticosteroids may still be indicated when preterm birth is anticipated.</p>
                <p>The associated glucose rise should be managed rather than using diabetes as a reason to withhold appropriate fetal treatment.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Induction and Caesarean Birth</h3>
              <div className="prose-rpl mt-4"><p>Women with diabetes have a higher likelihood of:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Induction of labour.","Operative vaginal birth.","Caesarean birth.","Anaesthetic complexity.","Postpartum haemorrhage.","Wound complications."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">These outcomes are influenced by:</p>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Fetal size.","Maternal weight.","Previous births.","Previous caesarean.","Hypertension.","Gestational age.","Cervical findings.","The reason for delivery."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">Diabetes alone does not make vaginal birth impossible.</p>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">The Psychological Burden Is Real</h3>
              <div className="prose-rpl mt-4"><p>Pregnancy diabetes care may require:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Multiple daily glucose checks.","Food recording.","Insulin decisions.","Fear of hypoglycaemia.","Fear of fetal complications.","Frequent appointments.","Repeated ultrasound examinations.","Pressure from family or social media.","Guilt after an unexpected high result.","Anxiety about birth and newborn glucose."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">Supportive care should include:</p>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Clear written plans.","Realistic goals.","A route for urgent questions.","Respectful nutrition counselling.","Psychological assessment when distress becomes persistent.","Avoidance of blame."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="mt-6 bg-surfacealt/60 border-l-2 border-prestige p-6"><p className="text-[15px] text-navy leading-relaxed"><strong>A glucose value is clinical information. It is not a judgement on the mother.</strong></p></div>
            </section>

            {/* What Diabetes Can Mean for the Baby */}
            <section id="fetal-risks" className="scroll-mt-32 mt-16">
              <Overline>Fetal Implications</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">What Diabetes Can Mean for the Baby</h2>
              <div className="prose-rpl mt-5">
                <p>The effect on the baby depends strongly on when the diabetes was present and how much glucose exposure occurred.</p>
                <p>Diabetes around conception and early organ development creates different concerns from gestational diabetes developing later.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Miscarriage</h3>
              <div className="prose-rpl mt-4">
                <p>Pre-existing diabetes with significant hyperglycaemia around conception is associated with an increased risk of pregnancy loss.</p>
                <p>Improving glucose before conception reduces risk.</p>
                <p>Once pregnancy has begun, treatment should focus on safe control and appropriate assessment—not retrospective blame.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Congenital Anomalies</h3>
              <div className="prose-rpl mt-4">
                <p>Major organs form early in pregnancy.</p>
                <p>Pre-existing diabetes with elevated glucose during this period increases the risk of congenital anomalies, particularly involving:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["The heart.","The brain and neural tube.","The spine.","The kidneys.","The skeleton.","Other organ systems."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>The risk rises progressively with worsening periconception glucose control.</p>
                <p>It is not possible to predict the outcome of an individual fetus from HbA1c alone.</p>
                <p>Detailed fetal assessment is therefore important. <Cite>1,4</Cite></p>
                <p>Conventional gestational diabetes developing in the second or third trimester does not usually create the same early structural-anomaly risk because major organ development has already occurred.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Large for Gestational Age and Macrosomia</h3>
              <div className="prose-rpl mt-4">
                <p>Maternal glucose crosses the placenta.</p>
                <p>Maternal insulin does not.</p>
                <p>The fetal pancreas responds to excess glucose by producing more insulin.</p>
                <p>Fetal insulin promotes growth and fat deposition.</p>
                <p>The baby may develop:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Increased abdominal size.","Increased shoulder and trunk fat.","Large-for-gestational-age growth.","Macrosomia.","Disproportionate growth even when the estimated weight is not extreme."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>The relationship is continuous.</p>
                <p>Risk can increase even at glucose levels below the threshold used to diagnose gestational diabetes.</p>
                <p>Improving glucose reduces excessive growth but cannot guarantee an average-size baby because growth is also influenced by genetics, maternal weight, placental function and gestational age. <Cite>1,6</Cite></p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Fetal Growth Restriction</h3>
              <div className="prose-rpl mt-4">
                <p>Diabetes does not always produce a large baby.</p>
                <p>Fetal growth restriction may occur when pre-existing diabetes is complicated by:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Kidney disease.","Hypertension.","Vascular disease.","Placental insufficiency.","Pre-eclampsia.","Another fetal or placental condition."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>A small fetus in a diabetic pregnancy requires careful interpretation.</p>
                <p>Restricting maternal nutrition further is not an automatic solution.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Shoulder Dystocia and Birth Injury</h3>
              <div className="prose-rpl mt-4">
                <p>A large fetal shoulder and trunk can make vaginal birth more difficult.</p>
                <p>Shoulder dystocia occurs when the head is born but the shoulders do not deliver with routine manoeuvres.</p>
                <p>Possible complications include:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Brachial-plexus injury.","Fracture.","Reduced oxygen during a difficult birth.","Severe maternal perineal trauma.","Postpartum haemorrhage."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>Ultrasound estimation of fetal weight is imprecise.</p>
                <p>No scan can predict every shoulder dystocia.</p>
                <p>Delivery planning therefore uses estimated size together with diabetes, maternal history, pelvic and obstetric factors, gestational age and the limitations of prediction. <Cite>3,9</Cite></p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Polyhydramnios</h3>
              <div className="prose-rpl mt-4">
                <p>High fetal glucose can increase fetal urine production and amniotic-fluid volume.</p>
                <p>Polyhydramnios may be associated with:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Preterm labour.","Malpresentation.","Umbilical-cord prolapse.","Unstable lie.","Maternal discomfort.","Postpartum haemorrhage."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>It can also be associated with fetal anomaly, infection or other conditions.</p>
                <p>A full assessment is required.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Preterm Birth</h3>
              <div className="prose-rpl mt-4"><p>The baby may be born early because of:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Spontaneous preterm labour.","Preterm rupture of membranes.","Pre-eclampsia.","Fetal growth restriction.","Poor maternal metabolic control.","Abnormal fetal surveillance.","Another maternal or fetal indication."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>Preterm birth can create respiratory, feeding, temperature and glucose difficulties.</p>
                <p>The risks of remaining pregnant must be balanced against the risks of prematurity.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Stillbirth</h3>
              <div className="prose-rpl mt-4">
                <p>Pre-existing diabetes increases stillbirth risk.</p>
                <p>Risk is greater when there is:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Poor glucose control.","Kidney or vascular disease.","Hypertension.","Fetal growth restriction.","Polyhydramnios.","Macrosomia.","Previous stillbirth.","Multiple maternal or fetal complications."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>A reassuring scan or CTG provides information about the period assessed.</p>
                <p>It cannot guarantee the remainder of the pregnancy.</p>
                <p>This is why fetal surveillance and timing of birth are individualised rather than relying on glucose treatment alone. <Cite>1,8</Cite></p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Neonatal Hypoglycaemia</h3>
              <div className="prose-rpl mt-4">
                <p>When the fetus has been exposed to excess glucose, the fetal pancreas may produce high levels of insulin.</p>
                <p>After birth, the maternal glucose supply stops abruptly while fetal insulin may remain high.</p>
                <p>The newborn’s glucose can fall.</p>
                <p>Risk is influenced by:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Maternal glucose near labour and birth.","Fetal hyperinsulinaemia.","Prematurity.","Birth weight.","Feeding effectiveness.","Other neonatal illness."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">Early feeding and planned newborn glucose testing reduce delay in recognition.</p>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Respiratory Difficulty</h3>
              <div className="prose-rpl mt-4">
                <p>Babies of women with diabetes may have an increased risk of respiratory difficulty.</p>
                <p>Risk is influenced by:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Prematurity.","Early-term birth.","Caesarean birth before labour.","Maternal glucose control.","Other neonatal conditions."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">This is one reason an early birth should occur only when the maternal or fetal benefit outweighs the neonatal cost.</p>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Other Newborn Problems</h3>
              <div className="prose-rpl mt-4"><p>The baby may also require assessment for:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Jaundice.","Polycythaemia.","Low calcium.","Low magnesium.","Cardiomyopathy.","Feeding difficulty.","Temperature instability.","Birth injury.","Respiratory distress."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>Most babies do not develop every complication.</p>
                <p>Newborn care is planned so that problems are detected and treated early. <Cite>1,4</Cite></p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Long-Term Metabolic Health</h3>
              <div className="prose-rpl mt-4"><p>Exposure to maternal hyperglycaemia is associated with an increased later risk of:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Obesity.","Abnormal glucose metabolism.","Type 2 diabetes.","Hypertension.","Other cardiometabolic concerns."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>Genes, family environment, nutrition and physical activity also influence later health.</p>
                <p>The pregnancy is therefore an opportunity to improve long-term family health—not a reason to label the child’s future as predetermined. <Cite>1</Cite></p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Most Babies Do Well</h3>
              <div className="prose-rpl mt-4">
                <p>Diabetes increases risk.</p>
                <p>It does not define the outcome.</p>
                <p>The most protective pathway combines:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Appropriate glucose control.","Adequate maternal nutrition.","Prevention of severe hypoglycaemia and ketoacidosis.","Maternal complication screening.","Detailed fetal assessment.","Growth surveillance.","Antenatal testing when indicated.","Planned timing and place of birth.","Prepared newborn care."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="mt-6 bg-surfacealt/60 border-l-2 border-prestige p-6"><p className="text-[15px] text-navy leading-relaxed"><strong>The purpose of discussing risk is to organise prevention—not to remove hope.</strong></p></div>
            </section>

            {/* Fetal Growth and Surveillance */}
            <section id="fetal-growth-surveillance" className="scroll-mt-32 mt-16">
              <Overline>Fetal Surveillance</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">Fetal Growth and Surveillance</h2>
              <div className="prose-rpl mt-5">
                <p>Fetal assessment should match the type, timing and severity of diabetes.</p>
                <p>A single standard schedule is not appropriate for every woman.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Early Dating and Viability</h3>
              <div className="prose-rpl mt-4"><p>Accurate gestational dating improves interpretation of:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Fetal growth.","Anatomy.","Surveillance.","Preterm-birth risk.","Timing of delivery."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">Early ultrasound may assess:</p>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Pregnancy location.","Viability.","Number of fetuses.","Gestational age.","Chorionicity in multiple pregnancy."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">The need and timing depend on the clinical situation.</p>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Detailed Structural Assessment</h3>
              <div className="prose-rpl mt-4">
                <p>Women with pre-existing type 1 or type 2 diabetes require detailed fetal structural assessment because hyperglycaemia may have been present during organ development.</p>
                <p>NICE recommends a 20-week ultrasound that includes examination of:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[{t:"The four-chamber cardiac view."},{t:"The outflow tracts."},{t:"The three-vessel view.",cite:"4"}].map((it,i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{it.t}{it.cite && <> <Cite>{it.cite}</Cite></>}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">Local practice may include a dedicated fetal echocardiogram when:</p>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Early glucose control was poor.","Cardiac views are incomplete or abnormal.","Another risk factor for congenital heart disease is present.","The specialist team recommends it."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>A normal detailed scan substantially reduces concern for many structural anomalies.</p>
                <p>It cannot exclude every genetic, functional or developmental condition.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Serial Growth and Amniotic-Fluid Assessment</h3>
              <div className="prose-rpl mt-4">
                <p>NICE recommends ultrasound assessment of fetal growth and amniotic-fluid volume every four weeks from 28 to 36 weeks for women with diabetes. <Cite>4</Cite></p>
                <p>Other healthcare systems individualise the schedule according to:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Type 1, type 2 or gestational diabetes.","Quality of glucose control.","Medication use.","Maternal weight.","Kidney or vascular disease.","Hypertension.","Previous growth abnormality.","Current fetal size.","Amniotic-fluid volume.","Multiple pregnancy."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>More scans are not automatically better.</p>
                <p>The interval should allow meaningful change to be detected while avoiding false precision.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Interpreting a Large Fetus</h3>
              <div className="prose-rpl mt-4"><p>When the estimated fetal weight or abdominal circumference is high, review:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["The accuracy and trend of gestational dating.","Previous growth measurements.","Maternal glucose pattern.","Maternal weight gain.","Genetics and previous birth weights.","Amniotic-fluid volume.","Fetal proportions.","The margin of error in ultrasound estimation."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>One high percentile does not prove uncontrolled diabetes.</p>
                <p>A rapidly rising abdominal circumference may still prompt closer review of post-meal glucose and treatment.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Interpreting a Small Fetus</h3>
              <div className="prose-rpl mt-4"><p>A small fetus may reflect:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Constitutional size.","Incorrect dating.","Placental insufficiency.","Maternal vascular disease.","Kidney disease.","Hypertension or pre-eclampsia.","Inadequate maternal intake.","Fetal anomaly.","Infection.","Another placental or fetal condition."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">Assessment may include:</p>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Serial growth.","Amniotic fluid.","Umbilical-artery Doppler.","Other Doppler studies when indicated.","Maternal blood pressure and laboratory tests.","Antenatal fetal surveillance.","Review of maternal nutrition."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Fetal-Movement Awareness</h3>
              <div className="prose-rpl mt-4">
                <p>The mother should know her baby’s usual movement pattern.</p>
                <p>She should seek prompt maternity assessment if:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Movements reduce.","Movements stop.","The pattern changes significantly.","She feels that something is wrong."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">She should not wait for:</p>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["The next diabetes appointment.","The next scan.","The next CTG.","The next meal or insulin dose."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">A home Doppler does not replace professional assessment.</p>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">What Antenatal Fetal Surveillance Can Do</h3>
              <div className="prose-rpl mt-4"><p>Surveillance may include:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Nonstress testing or CTG.","Biophysical profile.","Modified biophysical profile.","Amniotic-fluid assessment.","Doppler studies when growth restriction or placental disease is suspected."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">These tests can:</p>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Assess fetal status at the time of testing.","Identify a concerning heart-rate pattern.","Detect another obstetric problem.","Support a decision about delivery."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">What Surveillance Cannot Do</h3>
              <div className="prose-rpl mt-4">
                <p>A normal CTG cannot guarantee that the fetus will remain safe until the next test.</p>
                <p>A normal ultrasound cannot remove every diabetes-related risk.</p>
                <p>Surveillance has false-positive and false-negative limitations.</p>
                <p>Abnormal results must be interpreted with:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Gestational age.","Maternal condition.","Glucose control.","Fetal growth.","Amniotic fluid.","Doppler findings.","The complete clinical picture."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="mt-6 bg-surfacealt/60 border-l-2 border-prestige p-6"><p className="text-[15px] text-navy leading-relaxed"><strong>Surveillance provides information. It does not replace treatment or an appropriate delivery plan.</strong></p></div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">ACOG Approach to Surveillance</h3>
              <div className="prose-rpl mt-4"><p>ACOG states that:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[{t:"There is no consensus that fetal surveillance is required before 40 weeks for uncomplicated gestational diabetes controlled by diet alone."},{t:"Once- or twice-weekly surveillance may be considered from 32 weeks for medication-treated gestational diabetes without other complications."},{t:"Twice-weekly surveillance may be considered from 32 weeks for poorly controlled gestational diabetes."},{t:"Twice-weekly surveillance may be considered from 32 weeks for pre-existing diabetes."},{t:"Earlier surveillance may be appropriate when glucose control is poor, end-organ disease is present or another high-risk condition exists.",cite:"8"}].map((it,i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{it.t}{it.cite && <> <Cite>{it.cite}</Cite></>}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">These are suggestions rather than universal mandates.</p>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">NICE Approach to Surveillance</h3>
              <div className="prose-rpl mt-4"><p>NICE recommends:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[{t:"Growth and amniotic-fluid ultrasound every four weeks from 28 to 36 weeks."},{t:"No routine fetal-wellbeing testing before 38 weeks solely because diabetes is present, unless there is risk of fetal growth restriction."},{t:"Individualised surveillance when vascular disease, kidney disease or fetal growth restriction is present."},{t:"Fetal-wellbeing testing at 38 and 39 weeks when a woman with diabetes remains undelivered.",cite:"4"}].map((it,i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{it.t}{it.cite && <> <Cite>{it.cite}</Cite></>}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">The difference between guidelines reflects limited evidence, different healthcare systems and differing baseline delivery timing.</p>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Dr Tania’s Clinical Interpretation</h3>
              <div className="prose-rpl mt-4"><p>The surveillance plan should consider:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Diabetes type.","HbA1c and current glucose pattern.","Nutrition and treatment response.","Hypoglycaemia.","Ketones.","Medication or insulin requirement.","Maternal kidney, eye and vascular disease.","Blood pressure.","Previous stillbirth.","Fetal growth.","Amniotic-fluid volume.","Fetal movements.","Planned gestational age at birth.","The action that would follow an abnormal result."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>Testing should not be performed merely to create reassurance.</p>
                <p>It should be performed when the result can influence care.</p>
              </div>
            </section>

            {/* Timing and Mode of Birth */}
            <section id="timing-mode-of-birth" className="scroll-mt-32 mt-16">
              <Overline>Delivery Planning</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">Timing and Mode of Birth</h2>
              <div className="prose-rpl mt-5">
                <p>There is no single correct delivery date for every pregnancy with diabetes.</p>
                <p>The safest plan balances:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Stillbirth risk.","Neonatal respiratory and feeding risk.","Glucose control.","Diabetes type.","Medication or insulin requirement.","Maternal vascular or kidney disease.","Hypertension or pre-eclampsia.","Fetal growth.","Amniotic-fluid volume.","Fetal surveillance.","Previous pregnancy history.","Cervical findings.","Local neonatal facilities.","The woman’s informed preferences."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Why International Recommendations Differ</h3>
              <div className="prose-rpl mt-4"><p>Guidelines differ because:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["The supporting evidence is incomplete.","Stillbirth risk is difficult to predict precisely.","Definitions of “well controlled” vary.","Surveillance practices differ.","Neonatal resources differ.","Healthcare systems weigh early-term birth differently."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">The difference should be explained rather than hidden.</p>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">NICE Timing for Type 1 or Type 2 Diabetes</h3>
              <div className="prose-rpl mt-4">
                <p>For an otherwise uncomplicated pregnancy with type 1 or type 2 diabetes, NICE advises elective birth by induction of labour or caesarean birth when otherwise indicated between:</p>
                <p><strong>37 weeks 0 days and 38 weeks 6 days</strong></p>
                <p>Earlier birth may be considered when there are metabolic, maternal or fetal complications. <Cite>4</Cite></p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">NICE Timing for Gestational Diabetes</h3>
              <div className="prose-rpl mt-4">
                <p>NICE advises women with gestational diabetes to give birth no later than:</p>
                <p><strong>40 weeks 6 days</strong></p>
                <p>Earlier elective birth may be considered when maternal or fetal complications are present. <Cite>4</Cite></p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Common ACOG-Based Timing for Gestational Diabetes</h3>
              <div className="prose-rpl mt-4"><p>ACOG-based practice commonly uses:</p></div>

              <h4 className="font-serif text-xl text-navy tracking-tight leading-snug mt-8">Diet-Controlled Gestational Diabetes</h4>
              <div className="prose-rpl mt-4">
                <p>Birth is generally not recommended before:</p>
                <p><strong>39 weeks 0 days</strong></p>
                <p>Expectant management may continue to:</p>
                <p><strong>40 weeks 6 days</strong></p>
                <p>when glucose is well controlled and no other complication requires earlier birth.</p>
              </div>

              <h4 className="font-serif text-xl text-navy tracking-tight leading-snug mt-8">Medication-Controlled Gestational Diabetes</h4>
              <div className="prose-rpl mt-4">
                <p>Planned birth is commonly recommended between:</p>
                <p><strong>39 weeks 0 days and 39 weeks 6 days</strong></p>
                <p>when control is satisfactory and there are no additional complications.</p>
              </div>

              <h4 className="font-serif text-xl text-navy tracking-tight leading-snug mt-8">Poorly Controlled Gestational Diabetes</h4>
              <div className="prose-rpl mt-4">
                <p>Earlier delivery may be justified.</p>
                <p>The exact timing should be individualised according to:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Severity and duration of hyperglycaemia.","Failure of outpatient or inpatient treatment.","Fetal growth.","Amniotic fluid.","Fetal surveillance.","Maternal complications."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">Delivery in the late-preterm period should be reserved for a strong maternal or fetal indication because neonatal morbidity increases as gestational age falls. <Cite>3,8,10</Cite></p>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Common ACOG-Based Timing for Pre-Existing Diabetes</h3>
              <div className="prose-rpl mt-4">
                <p>For well-controlled pre-existing diabetes without vascular disease or another complication, planned birth is commonly placed in the:</p>
                <p><strong>39th week</strong></p>
                <p>Earlier birth—often within a broad window from approximately <strong>36 weeks 0 days to 38 weeks 6 days</strong>—may be considered when there is:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[{t:"Poor glucose control."},{t:"Vascular disease."},{t:"Kidney disease."},{t:"Hypertension or pre-eclampsia."},{t:"Previous stillbirth."},{t:"Fetal growth restriction."},{t:"Abnormal surveillance."},{t:"Another maternal or fetal indication.",cite:"3,8,10"}].map((it,i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{it.t}{it.cite && <> <Cite>{it.cite}</Cite></>}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>The broad window does not mean that every woman with one risk factor should deliver at 36 weeks.</p>
                <p>The clinical severity determines where within the window the balance changes.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Dr Tania’s Clinical Interpretation</h3>
              <div className="prose-rpl mt-4">
                <p>The delivery date should not be selected from diabetes type alone.</p>
                <p>I consider:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Whether hyperglycaemia was present around conception.","Current fasting and post-meal control.","Time in range when CGM is used.","Severe hypoglycaemia.","Ketones or ketoacidosis risk.","Insulin requirement and recent change.","Nutrition and maternal weight trajectory.","Blood pressure.","Kidney and vascular disease.","Previous pregnancy outcome.","Fetal growth pattern.","Fetal abdominal circumference.","Amniotic-fluid volume.","Fetal movements.","Antenatal-surveillance findings.","Cervical readiness.","Neonatal capacity.","The risks of waiting compared with the risks of early birth."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="mt-6 bg-surfacealt/60 border-l-2 border-prestige p-6"><p className="text-[15px] text-navy leading-relaxed"><strong>The safest delivery date is the point at which the benefit of remaining pregnant no longer outweighs the maternal and fetal risk of waiting.</strong></p></div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Diabetes Does Not Automatically Require Caesarean Birth</h3>
              <div className="prose-rpl mt-4"><p>Vaginal birth may be appropriate when:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["There is no separate obstetric contraindication.","The fetal presentation is suitable.","Estimated fetal size is acceptable after counselling.","Maternal and fetal conditions are stable.","Labour progress is satisfactory."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>Diabetes alone is not an indication for caesarean birth.</p>
                <p>Previous diabetic retinopathy is not an automatic contraindication to vaginal birth.</p>
                <p>A previous caesarean does not automatically prevent vaginal birth after caesarean. <Cite>4</Cite></p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Induction Does Not Mean Caesarean Birth</h3>
              <div className="prose-rpl mt-4">
                <p>Induction may be offered to prevent the risks of pregnancy continuing too long.</p>
                <p>The chance of vaginal birth depends on:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Previous vaginal birth.","Cervical findings.","Fetal position.","Maternal body mass index.","Fetal size.","Gestational age.","The induction method.","Maternal and fetal response."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>The purpose of induction should be explained clearly.</p>
                <p>It should not be presented as a failed attempt before it begins.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Suspected Macrosomia and Caesarean Counselling</h3>
              <div className="prose-rpl mt-4">
                <p>Ultrasound-estimated fetal weight has a meaningful margin of error.</p>
                <p>Counselling should include:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["The possibility that the estimate is too high or too low.","Risk of shoulder dystocia.","Previous birth history.","Maternal pelvic and obstetric factors.","Risks of induction.","Risks of planned caesarean birth.","Risks for future pregnancies after caesarean birth.","The woman’s values and preferences."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>ACOG advises that scheduled caesarean birth may be considered when estimated fetal weight is:</p>
                <p><strong>4,500 g or more in a woman with diabetes</strong></p>
                <p>This is a counselling threshold—not a guarantee that shoulder dystocia will occur below it or that injury will occur above it. <Cite>9</Cite></p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">When Earlier Birth May Be Required</h3>
              <div className="prose-rpl mt-4"><p>Earlier delivery may be necessary because of:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Diabetic ketoacidosis with ongoing maternal or fetal concern.","Severe or uncontrolled hypertension.","Pre-eclampsia.","Worsening kidney or vascular disease.","Persistent severe hyperglycaemia despite treatment.","Recurrent severe hypoglycaemia that cannot be managed safely.","Fetal growth restriction.","Abnormal Doppler.","Abnormal fetal surveillance.","Reduced fetal movements with concerning assessment.","Severe polyhydramnios with complications.","Preterm rupture of membranes.","Spontaneous preterm labour.","Another obstetric or fetal indication."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">The indication—not the label of diabetes alone—determines how early delivery is justified.</p>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Antenatal Corticosteroids</h3>
              <div className="prose-rpl mt-4">
                <p>Diabetes is not a contraindication to antenatal corticosteroids when they are indicated for fetal lung maturation.</p>
                <p>Steroids can cause substantial hyperglycaemia.</p>
                <p>The plan may require:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[{t:"More frequent glucose monitoring."},{t:"Temporary insulin introduction."},{t:"Temporary insulin-dose increase."},{t:"Ketone monitoring."},{t:"Inpatient management in selected women.",cite:"4"}].map((it,i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{it.t}{it.cite && <> <Cite>{it.cite}</Cite></>}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">Treatment should be planned proactively.</p>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Amniocentesis for Fetal Lung Maturity</h3>
              <div className="prose-rpl mt-4">
                <p>Demonstration of fetal lung maturity does not remove other neonatal risks of late-preterm or early-term birth.</p>
                <p>Amniocentesis for fetal lung maturity should not be used routinely to justify delivery when there is no maternal or fetal indication. <Cite>10</Cite></p>
              </div>
            </section>

            {/* Labour, Newborn Care and After Birth */}
            <section id="labour-newborn-postpartum" className="scroll-mt-32 mt-16">
              <Overline>Labour, Newborn & After Birth</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">Labour, Newborn Care and After Birth</h2>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Place of Birth</h3>
              <div className="prose-rpl mt-4"><p>Women with pre-existing diabetes or diabetes requiring complex treatment should give birth in a hospital capable of providing:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Maternal glucose and insulin management.","Continuous fetal monitoring when indicated.","Emergency obstetric care.","Anaesthesia.","Advanced neonatal resuscitation.","Newborn glucose monitoring.","Neonatal-unit care when required."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">NICE advises birth in a hospital where advanced neonatal resuscitation skills are available 24 hours a day. <Cite>4</Cite></p>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Before Labour or Planned Birth</h3>
              <div className="prose-rpl mt-4"><p>The written plan should address:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Date and method of birth.","Eating and drinking instructions.","Basal insulin.","Mealtime insulin.","Insulin-pump or automated-system settings.","Oral diabetes medicines.","Glucose-monitoring method.","Target glucose range.","Ketone testing when relevant.","Intravenous insulin and dextrose criteria.","Caesarean fasting instructions.","Neonatal feeding and glucose plan.","Immediate postpartum treatment change."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">The woman should not arrive for induction or caesarean without knowing what to do with her diabetes treatment.</p>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Glucose During Labour and Birth</h3>
              <div className="prose-rpl mt-4">
                <p>NICE recommends hourly capillary glucose monitoring during labour and birth, aiming to maintain glucose between:</p>
                <p><strong>4.0 and 7.0 mmol/L</strong></p>
                <p>This is approximately:</p>
                <p><strong>72 to 126 mg/dL</strong> <Cite>4</Cite></p>
                <p>Local protocols may use a different target range.</p>
                <p>The objective is to avoid:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Significant maternal hyperglycaemia.","Hypoglycaemia.","Ketosis.","Excess fetal glucose exposure immediately before birth."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Intravenous Insulin and Dextrose</h3>
              <div className="prose-rpl mt-4">
                <p>NICE advises considering intravenous insulin and dextrose from established labour for women with type 1 diabetes.</p>
                <p>It recommends intravenous insulin and dextrose when glucose cannot be maintained within the intrapartum target range. <Cite>4</Cite></p>
                <p>Not every woman with gestational diabetes requires an insulin infusion.</p>
                <p>The decision depends on:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Diabetes type.","Current treatment.","Labour duration.","Oral intake.","Glucose pattern.","Local protocol."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Insulin Pumps and Automated Insulin Delivery During Labour</h3>
              <div className="prose-rpl mt-4"><p>Selected women with type 1 diabetes may continue an insulin pump or automated insulin-delivery system during labour when:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["They are able and willing to manage it.","The team has an agreed protocol.","Glucose remains within target.","The device is functioning.","There is a clear backup plan."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">Intravenous insulin may be required if:</p>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Glucose is not controlled.","Ketones develop.","The pump fails.","The woman cannot manage the system.","General anaesthesia or another clinical circumstance changes safety."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">Technology should simplify care—not create uncertainty during an emergency.</p>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Fetal Monitoring During Labour</h3>
              <div className="prose-rpl mt-4">
                <p>Continuous CTG is commonly advised for women with pre-existing diabetes and for gestational diabetes with additional risk.</p>
                <p>The plan considers:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Diabetes type and control.","Induction.","Fetal growth.","Amniotic fluid.","Gestational age.","Meconium.","Oxytocin use.","Maternal and fetal condition."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Shoulder Dystocia Preparedness</h3>
              <div className="prose-rpl mt-4">
                <p>When fetal size or diabetes increases concern, the delivery team should be prepared for shoulder dystocia.</p>
                <p>Preparation includes:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Experienced staff.","Clear communication.","Immediate access to standard manoeuvres.","Avoidance of excessive traction.","Neonatal assessment after birth."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">Preparation does not mean that shoulder dystocia is expected to occur.</p>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Caesarean Birth</h3>
              <div className="prose-rpl mt-4"><p>For planned caesarean birth, the diabetes plan should address:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Preoperative fasting.","Basal insulin.","Morning insulin or medicines.","Glucose-monitoring frequency.","Intravenous treatment when needed.","Postoperative nutrition.","Postpartum dose reduction.","Wound and infection risk.","Thrombosis prevention according to the full risk profile."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">The Baby Should Usually Remain With the Mother</h3>
              <div className="prose-rpl mt-4"><p>Babies of women with diabetes should remain with their mothers unless:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["The baby is clinically unwell.","Prematurity requires additional care.","Glucose cannot be maintained.","Respiratory distress is present.","Feeding support exceeds what the postnatal ward can provide.","Another neonatal condition requires admission."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>Rooming-in supports feeding and bonding.</p>
                <p>It should be accompanied by reliable newborn observation.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Early Feeding</h3>
              <div className="prose-rpl mt-4"><p>NICE recommends feeding the baby:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[{t:"As soon as possible after birth—ideally within ",strong:"30 minutes",after:"."},{t:"Then every ",strong:"2 to 3 hours",after:" until feeding maintains safe pre-feed glucose.",cite:"4"}].map((it,i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{it.t}<strong>{it.strong}</strong>{it.after}{it.cite && <> <Cite>{it.cite}</Cite></>}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>Breastfeeding should be supported.</p>
                <p>Expressed colostrum or additional feeding strategies may be discussed when clinically appropriate.</p>
                <p>Antenatal colostrum expression is not suitable for every pregnancy and should follow individual obstetric advice.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Newborn Glucose Testing</h3>
              <div className="prose-rpl mt-4">
                <p>NICE recommends routine newborn glucose testing at:</p>
                <p><strong>2 to 4 hours after birth</strong></p>
                <p>Testing may occur earlier if symptoms are present. <Cite>4</Cite></p>
                <p>The exact treatment threshold and pathway should follow a validated local neonatal protocol.</p>
                <p>Newborn glucose meters and adult diabetes thresholds are not interchangeable.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Signs of Neonatal Hypoglycaemia</h3>
              <div className="prose-rpl mt-4"><p>Possible signs include:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Jitteriness.","Poor feeding.","Lethargy.","Abnormal temperature.","Weak cry.","Breathing difficulty.","Reduced tone.","Seizure."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>Some babies with low glucose have no obvious symptoms.</p>
                <p>This is why planned testing is required.</p>
                <p>Symptomatic neonatal hypoglycaemia requires immediate assessment and treatment.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Neonatal-Unit Admission</h3>
              <div className="prose-rpl mt-4"><p>Admission may be required because of:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Persistent or symptomatic hypoglycaemia.","Prematurity.","Respiratory distress.","Feeding difficulty.","Need for intravenous fluids.","Suspected infection.","Birth injury.","Cardiomyopathy or congenital heart disease.","Significant jaundice or polycythaemia.","Another neonatal concern."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>A neonatal-unit admission does not mean that long-term harm is inevitable.</p>
                <p>It means that the baby requires a level of monitoring or treatment not available on the routine postnatal ward.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Maternal Insulin Immediately After Birth</h3>
              <div className="prose-rpl mt-4">
                <p>Placental delivery causes insulin resistance to fall rapidly.</p>
                <p>Women with type 1 diabetes still require insulin, but the dose usually needs immediate reduction.</p>
                <p>Women with insulin-treated type 2 diabetes also require prompt reassessment.</p>
                <p>The postpartum plan should be written before birth.</p>
                <p>ADA notes that insulin requirements may be substantially lower immediately postpartum and then move back toward pre-pregnancy requirements over the following one to two weeks. <Cite>1</Cite></p>
                <p>Continuing the full late-pregnancy dose after birth can cause severe hypoglycaemia.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Gestational Diabetes Treatment After Birth</h3>
              <div className="prose-rpl mt-4">
                <p>Women treated for gestational diabetes usually stop glucose-lowering medication immediately after birth.</p>
                <p>Glucose should be checked to confirm that severe hyperglycaemia has resolved.</p>
                <p>Persistent high glucose may indicate:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Previously unrecognised type 2 diabetes.","Developing type 1 diabetes.","Monogenic diabetes.","Ongoing metabolic stress.","Steroid or illness effects."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">The diagnosis should be clarified rather than assuming every case disappears.</p>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Breastfeeding</h3>
              <div className="prose-rpl mt-4">
                <p>Breastfeeding is recommended and should be supported for women with diabetes.</p>
                <p>Potential benefits include:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Infant nutrition and immune protection.","Maternal metabolic benefit.","Reduced later type 2 diabetes risk after gestational diabetes."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">Breastfeeding may increase maternal hypoglycaemia risk, particularly in insulin-treated women.</p>
              <p className="mt-3 text-[15px] text-stone2 leading-relaxed">The plan may require:</p>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {[{t:"Insulin reduction."},{t:"Glucose checks around feeds."},{t:"Food available during or before feeds."},{t:"Review of overnight patterns."},{t:"Reassessment of medicines for breastfeeding safety.",cite:"1,4"}].map((it,i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{it.t}{it.cite && <> <Cite>{it.cite}</Cite></>}</span></li>))}
              </ul>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Medicines After Birth</h3>
              <div className="prose-rpl mt-4"><p>Postpartum medicines should be reviewed according to:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Diabetes type.","Breastfeeding.","Kidney and liver function.","Blood pressure.","Vascular disease.","Future pregnancy plans."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>NICE permits metformin to be continued or resumed in breastfeeding women with pre-existing type 2 diabetes.</p>
                <p>Other glucose-lowering medicines require individual review. <Cite>4</Cite></p>
                <p>Medicines stopped before pregnancy—such as some blood-pressure drugs or statins—should not be restarted automatically without checking breastfeeding compatibility and the current indication.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Postpartum Testing After Gestational Diabetes</h3>
              <div className="prose-rpl mt-4">
                <p>ADA recommends a fasting <strong>75 g oral glucose-tolerance test at 4 to 12 weeks postpartum</strong>, interpreted using non-pregnancy diagnostic criteria. <Cite>1,2</Cite></p>
                <p>The OGTT is preferred over HbA1c at this stage because HbA1c may be affected by:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Pregnancy-related red-cell turnover.","Blood loss at delivery.","The glucose pattern of the preceding three months."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">The test can identify:</p>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Persistent diabetes.","Impaired fasting glucose.","Impaired glucose tolerance.","Normal postpartum glucose."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">A normal fasting glucose alone may miss impaired glucose tolerance.</p>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Lifelong Follow-Up After Gestational Diabetes</h3>
              <div className="prose-rpl mt-4">
                <p>A history of gestational diabetes increases the future risk of:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Recurrent gestational diabetes.","Prediabetes.","Type 2 diabetes.","Cardiovascular risk."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>ADA recommends lifelong screening every:</p>
                <p><strong>1 to 3 years</strong> <Cite>1</Cite></p>
                <p>Follow-up may use:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Fasting glucose.","HbA1c.","A 75 g OGTT when indicated.","Other non-pregnancy diagnostic testing."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">The result should be connected to a prevention plan rather than filed and forgotten.</p>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Postpartum Nutrition</h3>
              <div className="prose-rpl mt-4"><p>After birth, nutrition should account for:</p></div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Breastfeeding.","Sleep disruption.","Recovery from vaginal or caesarean birth.","Anaemia.","Wound healing.","Hypoglycaemia.","Maternal weight.","Long-term diabetes prevention or treatment.","Cultural postpartum foods.","Available household support."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="prose-rpl mt-6">
                <p>A restrictive pregnancy diet should not continue automatically.</p>
                <p>The postpartum plan should be rebuilt for the mother’s new metabolic and nutritional needs.</p>
              </div>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">Emotional and Practical Follow-Up</h3>
              <div className="prose-rpl mt-4">
                <p>Postpartum diabetes care can fail because the mother is caring for the baby while her own appointments, meals, sleep and medicines become fragmented.</p>
                <p>Follow-up should include:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Psychosocial assessment.","Depression and anxiety screening.","Support for diabetes self-care.","Contraception.","Breastfeeding support.","Transition back to the long-term diabetes team.","Planning before another pregnancy."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>

              <h3 className="font-serif text-2xl text-navy tracking-tight leading-snug mt-10">The Next Pregnancy Begins Before Conception</h3>
              <div className="prose-rpl mt-4">
                <p>Women with previous gestational diabetes or pre-existing diabetes should seek preconception care before another pregnancy.</p>
                <p>The plan may include:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["Glucose testing.","HbA1c.","Medication review.","Kidney assessment.","Retinal assessment.","Blood-pressure control.","Folic-acid advice.","Nutrition and weight review.","Contraception until treatment is pregnancy-compatible.","Review of the previous pregnancy and newborn outcome."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <div className="mt-6 bg-surfacealt/60 border-l-2 border-prestige p-6"><p className="text-[15px] text-navy leading-relaxed"><strong>Birth ends the pregnancy. It does not end the mother’s metabolic care.</strong></p></div>

              <p className="mt-8 text-[15px] text-stone2 leading-relaxed">
                The final part will add the Frequently Asked Questions, closing section, Medical Disclaimer, Evidence Base and complete technical testing requirements.
              </p>
            </section>

            {/* --- PART 4 END --------------------------------------- */}

            {/* --- PART 5 START ------------------------------------- */}

            {/* Frequently Asked Questions */}
            <section id="frequently-asked-questions" className="scroll-mt-32 mt-16">
              <Overline>FAQ</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                Frequently Asked Questions
              </h2>
              <div className="mt-8" data-testid="pwd-faq-list">
                {FAQ_ITEMS.map((f, i) => (
                  <FaqRow key={i} q={f.q} a={f.a} />
                ))}
              </div>
            </section>

            {/* Closing Section */}
            <section id="closing" className="scroll-mt-32 mt-16">
              <Overline>Closing</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">
                A High Glucose Result Is Not a Verdict. It Is a Signal to Build the Right Plan.
              </h2>
              <div className="prose-rpl mt-6">
                <p>Pregnancy with diabetes requires more than lowering one number.</p>
                <p>The diabetes type must be identified.</p>
                <p>The current glucose pattern must be reconstructed.</p>
                <p>Ketone and hypoglycaemia risks must remain visible.</p>
                <p>Nutrition should begin as active treatment—not as an afterthought.</p>
                <p>Its effect should be measured honestly.</p>
                <p>Insulin should be used whenever physiology requires it.</p>
                <p>It should be reduced when monitored evidence shows that the requirement has fallen.</p>
                <p>It should never be stopped merely to prove that diet can work.</p>
                <p>The objective is to enter the remainder of pregnancy with:</p>
              </div>
              <ul className="mt-4 space-y-2 text-[15px] text-stone2 leading-relaxed" role="list">
                {["The diabetes type clarified as accurately as possible.","The HbA1c interpreted without allowing it to replace current glucose.","Fasting, post-meal and overnight patterns understood.","A personalised nutrition plan that remains adequate for pregnancy.","Ketone and sick-day guidance understood.","Hypoglycaemia prevented and treated promptly.","Medication and insulin matched to the actual physiology.","Maternal kidney, eye, blood-pressure and vascular risks assessed.","Fetal structure, growth and wellbeing evaluated appropriately.","A provisional delivery window agreed.","Labour glucose management written in advance.","Early newborn feeding and glucose testing planned.","Immediate postpartum treatment reduction anticipated.","Long-term maternal metabolic follow-up arranged."].map((i)=>(<li key={i} className="flex items-start gap-3"><span className="mt-2.5 h-1 w-1 rounded-full bg-prestige shrink-0" aria-hidden="true"/><span>{i}</span></li>))}
              </ul>
              <p className="mt-6 text-[15px] text-stone2 leading-relaxed">No clinician can guarantee the outcome.</p>
              <div className="mt-6 bg-surfacealt/60 border-l-2 border-prestige p-6">
                <p className="text-[15px] text-navy leading-relaxed"><strong>The strongest diabetes care does not choose between nutrition and medicine. It uses nutrition intensively, medicine precisely and monitoring honestly.</strong></p>
              </div>

              <div className="mt-10 bg-navy text-white p-8">
                <div className="font-serif text-2xl leading-tight">Schedule a Pregnancy Diabetes Consultation</div>
                <p className="text-white/70 text-sm mt-3 leading-relaxed">Bring your glucose records, reports, medicines and a three-day food log. Leave with a nutrition, monitoring, treatment and pregnancy plan.</p>
                <div className="mt-5">
                  <PrimaryButton to="/contact?type=appointment&reason=diabetes" testid="pwd-closing-cta">Schedule a Pregnancy Diabetes Consultation</PrimaryButton>
                </div>
              </div>
            </section>

            {/* Medical Disclaimer */}
            <section id="medical-disclaimer" className="scroll-mt-32 mt-16">
              <Overline>Medical Disclaimer</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">Medical Disclaimer</h2>
              <div className="prose-rpl mt-6" data-testid="pwd-disclaimer">
                <p>This page provides general educational information about type 1 diabetes, type 2 diabetes, gestational diabetes and other forms of hyperglycaemia during pregnancy.</p>
                <p>It does not replace individual diagnosis, nutritional assessment, glucose interpretation, ketone assessment, medication or insulin adjustment, maternal complication screening, fetal surveillance, emergency treatment or delivery planning.</p>
                <p>Do not start, stop or alter insulin or another glucose-lowering medicine solely because of information on this page. Insulin interruption in type 1 diabetes or insulin-deficient states can cause diabetic ketoacidosis. Pregnancy-related ketoacidosis may occur at glucose levels lower than expected.</p>
                <p>Persistent vomiting, inability to drink, abdominal pain, rapid or deep breathing, moderate or high ketones, marked weakness, confusion, loss of consciousness, seizure, recurrent severe hypoglycaemia, reduced fetal movements, severe headache, visual disturbance, bleeding, fluid leakage, contractions or feeling seriously unwell may require urgent hospital assessment.</p>
              </div>
            </section>

            {/* Evidence Base */}
            <section id="evidence-base" className="scroll-mt-32 mt-16">
              <Overline>Evidence Base</Overline>
              <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight leading-tight mt-3">Evidence Base</h2>
              <ol className="mt-8 space-y-5 text-[14px] text-stone2 leading-relaxed" data-testid="pwd-evidence-base">
                <li id="ref-1" className="scroll-mt-32"><span className="mono text-[11px] text-prestige mr-2">[1]</span>American Diabetes Association Professional Practice Committee for Diabetes. 15. Management of diabetes in pregnancy: Standards of Care in Diabetes—2026. <em>Diabetes Care.</em> 2026;49(Suppl 1):S321–S338. doi:10.2337/dc26-S015.</li>
                <li id="ref-2" className="scroll-mt-32"><span className="mono text-[11px] text-prestige mr-2">[2]</span>American Diabetes Association Professional Practice Committee for Diabetes. 2. Diagnosis and classification of diabetes: Standards of Care in Diabetes—2026. <em>Diabetes Care.</em> 2026;49(Suppl 1):S27–S49. doi:10.2337/dc26-S002.</li>
                <li id="ref-3" className="scroll-mt-32"><span className="mono text-[11px] text-prestige mr-2">[3]</span>American College of Obstetricians and Gynecologists. Gestational Diabetes Mellitus. ACOG Practice Bulletin No. 190. <em>Obstetrics &amp; Gynecology.</em> 2018;131:e49–e64. Reaffirmed 2024; with July 2024 Clinical Practice Update. American College of Obstetricians and Gynecologists. Pregestational Diabetes Mellitus. ACOG Practice Bulletin No. 201. <em>Obstetrics &amp; Gynecology.</em> 2018;132:e228–e248. Reaffirmed 2025; with July 2024 Clinical Practice Update.</li>
                <li id="ref-4" className="scroll-mt-32"><span className="mono text-[11px] text-prestige mr-2">[4]</span>National Institute for Health and Care Excellence. <em>Diabetes in pregnancy: management from preconception to the postnatal period.</em> NICE Guideline NG3. Published 2015; last updated 16 December 2020.</li>
                <li id="ref-5" className="scroll-mt-32"><span className="mono text-[11px] text-prestige mr-2">[5]</span>Battelino T, Danne T, Bergenstal RM, et al. Clinical targets for continuous glucose monitoring data interpretation: recommendations from the International Consensus on Time in Range. <em>Diabetes Care.</em> 2019;42(8):1593–1603. doi:10.2337/dci19-0028.</li>
                <li id="ref-6" className="scroll-mt-32"><span className="mono text-[11px] text-prestige mr-2">[6]</span>HAPO Study Cooperative Research Group. Hyperglycemia and adverse pregnancy outcomes. <em>New England Journal of Medicine.</em> 2008;358(19):1991–2002. doi:10.1056/NEJMoa0707943.</li>
                <li id="ref-7" className="scroll-mt-32"><span className="mono text-[11px] text-prestige mr-2">[7]</span>Spencer NR, Clark SM, Harirah HM. Management of diabetic ketoacidosis in pregnancy. <em>Clinical Obstetrics and Gynecology.</em> 2023;66(1):186–195. doi:10.1097/GRF.0000000000000758.</li>
                <li id="ref-8" className="scroll-mt-32"><span className="mono text-[11px] text-prestige mr-2">[8]</span>American College of Obstetricians and Gynecologists. Indications for outpatient antenatal fetal surveillance. ACOG Committee Opinion No. 828. <em>Obstetrics &amp; Gynecology.</em> 2021;137:e177–e197. Reaffirmed 2024.</li>
                <li id="ref-9" className="scroll-mt-32"><span className="mono text-[11px] text-prestige mr-2">[9]</span>American College of Obstetricians and Gynecologists. Macrosomia. ACOG Practice Bulletin No. 216. <em>Obstetrics &amp; Gynecology.</em> 2020;135:e18–e35. Reaffirmed 2023.</li>
                <li id="ref-10" className="scroll-mt-32"><span className="mono text-[11px] text-prestige mr-2">[10]</span>American College of Obstetricians and Gynecologists; Society for Maternal-Fetal Medicine. Medically indicated late-preterm and early-term deliveries. ACOG Committee Opinion No. 831. <em>Obstetrics &amp; Gynecology.</em> 2021;138:e35–e39. Interim update reflected in current ACOG guidance.</li>
              </ol>
            </section>

            {/* --- PART 5 END --------------------------------------- */}
          </div>
        </div>
      </section>
    </div>
  );
}
